import { CoachDevelopmentCard } from "@/components/coach/CoachDevelopmentCard";
import { BiThemeReviewPanel } from "@/components/coach/BiThemeReviewPanel";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, CalendarPlus, CheckCircle2, ChevronDown, FileText, Layers3, MessageSquare, ShieldCheck, Sparkles, Upload, X } from "lucide-react";
import { CoachSharedSessionView } from "@/components/coach/postMeeting/CoachSharedSessionView";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCoachHealthDestinationAuthorization } from "@/hooks/useCoachHealthDestinationAuthorization";
import { useCoachLeaderCallSchedule } from "@/hooks/useCoachLeaderCallSchedule";
import { format } from "date-fns";
import { toast } from "sonner";
import { CoachLayout } from "@/components/layout/CoachLayout";
import { ContinuityPanel } from "@/components/continuity/ContinuityPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useCoachNotes } from "@/hooks/useCoachNotes";
import { useCoachLeaderCarryForward } from "@/hooks/useCoachLeaderCarryForward";
import { useCoachNewEvidence } from "@/hooks/useCoachNewEvidence";
import { useCoachReviewArtifactViews } from "@/hooks/useCoachReviewArtifactViews";
import {
  useApproveCoachPracticeFocusSuggestion,
  useCoachPracticeFocusReview,
  type CoachPracticeFocusReviewItem,
} from "@/hooks/useCoachPracticeFocusReview";
import {
  useCoachSessionPrepReceipt,
  useCoachSessionPrepData,
} from "@/hooks/useCoachSessionPrepData";
import { useThroughlinePresentation } from "@/hooks/useThroughlinePresentation";
import { useThroughlineSelectionSync } from "@/hooks/useThroughlineSelectionSync";
import { supabase } from "@/integrations/supabase/client";
import { getAvatarInitials } from "@/utils/avatar";
import { countItemsSince, getCoachLeaderLastSeenAt, markCoachLeaderSeenNow } from "@/lib/coachNewEvidence";
import { demoLeaders, demoTranscriptAnalysis } from "@/data/demoData";
import { EvidenceSnapshotSection } from "@/components/coach/sessionPrep/EvidenceSnapshotSection";
import {
  type EvidenceTimelineItem,
} from "@/components/coach/sessionPrep/EvidenceTimelineSection";
import { CoachNotesSection } from "@/components/coach/sessionPrep/CoachNotesSection";
import { CoachLeaderDetailWorkspace } from "@/components/coach/leader-detail/CoachLeaderDetailWorkspace";
import { CoachLeaderLongitudinalInsight } from "@/components/coach/leader-detail/CoachLeaderLongitudinalInsight";
import { CoachVeeraPrepBriefCard } from "@/components/coach/sessionPrep/CoachVeeraPrepBriefCard";
import { CoachSessionPrepInterventionOptionsCard } from "@/components/coach/sessionPrep/CoachSessionPrepInterventionOptionsCard";
import { LeadershipRecordTimeline } from "@/components/coach/sessionPrep/LeadershipRecordTimeline";
import {
} from "@/lib/throughlineAdapter";
import {
  dedupeEvidenceLinks,
  resolveCoachEvidenceReference,
  toCoachEvidenceLink,
  type CoachEvidenceCanonicalIdentity,
} from "@/lib/coachEvidenceResolver";
import { authorizeCoachEvidenceReference } from "@/lib/authorizeCoachEvidenceReference";
import {
  getCoachReviewArtifactKey,
  type CoachReviewResolvedSource,
} from "@/lib/coachReviewArtifacts";
import { buildCoachTranscriptReviewRoute } from "@/lib/coachTranscriptReviewRoute";
import { buildCoachPostMeetingRoute } from "@/lib/coachPostMeetingRoute";
import { applyCoachNoticingViewState } from "@/lib/behavior/viewModels/applyCoachNoticingViewState";
import {
  buildCoachSessionPrepRuntime,
  buildCoachSessionPrepUnsaidSignals,
  type CoachSessionPrepAgreementRecord,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepRuntime";
import { EvidenceDetailDialog } from "@/components/coach/EvidenceDetailDialog";
import { useCoachRuntimeContext } from "@/hooks/useCoachRuntimeContext";
import { useCoachVeeraPanel } from "@/hooks/useCoachVeeraPanel";
import { trackCoachVeeraEvidenceOpened, trackCoachVeeraOpened } from "@/lib/behavior/coachVeeraTelemetry";
import { trackCoachBehavioralObservationViewed } from "@/lib/behavior/observationTelemetry";
import { EmergingPatternsSection } from "@/components/coach/sessionPrep/EmergingPatternsSection";
import { buildCoachSessionContinuityRuntime } from "@/lib/behavior/viewModels/buildContinuityRuntime";
import { buildCoachThroughlineSelectedDay } from "@/lib/behavior/viewModels/buildCoachThroughlineSelectedDay";
import { CoachThroughlineSection } from "@/components/coach/sessionPrep/CoachThroughlineSection";
import { LeaderArcSection } from "@/components/coach/sessionPrep/LeaderArcSection";
import { useCoachAttentionSignals } from "@/hooks/useCoachAttentionSignals";
import { useContinuityMomentToast } from "@/hooks/useContinuityMomentToast";
import { useCoachingRecordSummary } from "@/hooks/useContinuityRecordSummary";
import { buildCoachPortfolioIntelligence } from "@/lib/behavior/viewModels/buildCoachPortfolioIntelligence";
import { CoachPortfolioIntelligenceCard } from "@/components/coach/CoachPortfolioIntelligenceCard";
import type { VisibilityState } from "@/lib/behavior/viewModels/coachSessionPrepVeera";
import {
  COACH_ACTIVE_LEADER_PLAN_TYPE,
  COACH_LED_PROCESSED_MEETING_LIMIT,
  MEETING_PROCESSING_EVENT_TYPE,
  MEETING_PROCESSING_SOURCE_EXTRA_PACK,
  MEETING_PROCESSING_SOURCE_INCLUDED,
  countRemainingExtraMeetingCredits,
  hasManualUnlimitedProcessingAccess,
  resolveLeaderBillingPeriod,
} from "@/lib/soloLeaderBilling";
import {
  DEFER_BILLING_TRANSFER,
  KEEP_SOLO_BILLING,
  TRANSFER_TO_COACH,
} from "@/lib/billingOwnership";
import { cn } from "@/lib/utils";
import { buildLeaderDetailDecisionPresentation } from "@/lib/coach/leaderDetailDecisionPresentation";
import { buildCoachLeaderDetailSourceReadiness } from "@/lib/coachLeaderDetail/buildCoachLeaderDetailSourceReadiness";
import {
  adaptLeaderDetailDecisionPresentation,
  type CoachLeaderDetailPrimaryAction,
  type CoachLeaderDetailRecentContextFact,
} from "@/lib/coachLeaderDetail/buildCoachLeaderDetailPresentation";
import {
  buildCoachLeaderLongitudinalRead,
  type CoachLeaderLongitudinalEpisode,
  type CoachLeaderLongitudinalRead,
} from "@/lib/coachLeaderDetail/buildCoachLeaderLongitudinalRead";
import {
  buildCoachLeaderLongitudinalAnalytics,
  COACH_LEADER_LONGITUDINAL_EVENTS,
  type CoachLeaderLongitudinalAnalyticsAction,
  type CoachLeaderLongitudinalAnalyticsInput,
} from "@/lib/coachLeaderDetail/coachLeaderLongitudinalAnalytics";
import { capturePostHogEvent } from "@/utils/posthog";


type ReflectionItem = {
  id: string;
  created_at: string | null;
  text: string | null;
  title: string;
  source: "post_reflection" | "shared_reflection" | "assessment_reflection";
  link: string | null;
  meeting_id: string | null;
};

type TranscriptItem = {
  id: string;
  created_at: string;
  title: string;
  summary: string | null;
  source: "transcript";
  link: string;
  meeting_id: string | null;
  analysis_status: string | null;
  coach_verified: boolean | null;
};

type ArtifactItem = {
  id: string;
  created_at: string;
  title: string;
  type: string;
  source: "artifact";
  link: string | null;
};

type MeetingFeedbackItem = {
  id: string;
  meeting_id: string;
  attendee_count: number | null;
  meeting_type: string | null;
  was_good_fit: boolean;
  feedback_reason: string | null;
  created_at: string;
};

type LeaderLedgerEventItem = {
  id: string;
  created_at: string;
  event_type: string;
  source_type: string;
  actor_role: string | null;
  confidence_level: string | null;
  metadata: Record<string, unknown> | null;
};

// AgreementConversationRecord and AgreementRecord defined below (after CoachLensContext)

type SelectedEvidenceSession = {
  evidence: EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null };
  returnToQueue: boolean;
};

type AgreementConversationRecord = CoachSessionPrepAgreementRecord["conversationRecords"][number];
type AgreementRecord = CoachSessionPrepAgreementRecord;

function agreementRecordSignature(record: AgreementRecord | null | undefined) {
  if (!record) return "";
  return JSON.stringify({
    namedAgreements: record.namedAgreements,
    conversationRecords: record.conversationRecords.map((item) => ({
      id: item.id,
      source: item.source,
      created_at: item.created_at,
      summary: item.summary,
      transcriptUploadId: item.transcriptUploadId ?? null,
      tags: item.tags ?? [],
    })),
  });
}

function toDisplayError(error: unknown) {
  if (!error) return "Unknown error";
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  const err = error as { message?: string; details?: string; hint?: string; code?: string };
  if (err.message || err.details || err.hint) {
    return [err.message, err.details, err.hint].filter(Boolean).join(" | ");
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown error";
  }
}

const formatTimestamp = (value?: string | null) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return format(date, "MMM d, yyyy · h:mm a");
};

const trimSingleLine = (value: string, max = 340) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}...`;
};

const normalizeCarryForwardQuestion = (value: string) => value
  .replace(/^question:\s*/i, "")
  .replace(/\s+/g, " ")
  .trim()
  .toLocaleLowerCase();

const mergeCarryForwardQuestion = (existingNote: string, question: string) => {
  const normalizedQuestion = normalizeCarryForwardQuestion(question);
  const blocks = existingNote
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (blocks.some((block) => normalizeCarryForwardQuestion(block) === normalizedQuestion)) {
    return null;
  }
  return [...blocks, question.trim()].filter(Boolean).join("\n\n");
};

const firstSentence = (value?: string | null) => {
  if (!value) return "";
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const match = normalized.match(/(.+?[.!?])(\s|$)/);
  return match?.[1]?.trim() || normalized;
};

const parseCanonicalBehaviorName = (observation: { source: string | null; moment: string } | null | undefined) => {
  if (!observation || observation.source !== "Coach Veera") return null;
  const match = observation.moment.match(/^The canonical behavioral read right now is (.+)\.$/);
  return match?.[1]?.trim() || null;
};

const NOTE_DATE_FMT = "EEE, MMM d";
const NEW_EVIDENCE_WINDOW_DAYS = 180;
const DEFAULT_COACH_VEERA_QUESTION = "What changed since the last coaching meeting?";
const LEGACY_SECTION_ALIASES: Record<string, string> = {
  journey: "section-throughline",
  "section-journey": "section-throughline",
  noticing: "section-snapshot",
  "section-noticing": "section-snapshot",
};

type LeaderWorkspaceView = "review" | "throughline" | "patterns" | "brief" | "arc" | "record";

const LEADER_WORKSPACE_LABELS: Record<LeaderWorkspaceView, { title: string; eyebrow: string; description: string }> = {
  review: {
    title: "Review Work",
    eyebrow: "Leader workspace",
    description: "Transcripts, new evidence, and pending review items for this leader.",
  },
  throughline: {
    title: "Coach Throughline",
    eyebrow: "Session prep",
    description: "How the leader pattern has been moving across the recent coaching window.",
  },
  patterns: {
    title: "Emerging Patterns",
    eyebrow: "Current read",
    description: "The behavioral signals Coach Veera is organizing from leader evidence.",
  },
  brief: {
    title: "Next Session Brief",
    eyebrow: "Next conversation",
    description: "What changed, what is unresolved, and what to probe next.",
  },
  arc: {
    title: "Leader Arc & Moves",
    eyebrow: "Coaching moves",
    description: "The longer arc, repeated patterns, strengths, and possible coaching interventions.",
  },
  record: {
    title: "Leadership Record",
    eyebrow: "Longitudinal record",
    description: "Continuity, agreements, and conversation records that should carry forward.",
  },
};

const LEADER_WORKSPACE_DESTINATIONS: ReadonlyArray<{
  view: "brief" | "throughline" | "record";
  title: string;
  description: string;
}> = [
  {
    view: "brief",
    title: "Prepare next session",
    description: "Prepare for the next conversation using current context and a private coach note.",
  },
  {
    view: "throughline",
    title: "What's changing",
    description: "See the relationship-level context that may be changing across recorded coaching work.",
  },
  {
    view: "record",
    title: "Evidence & record",
    description: "Review durable coaching context, agreements, and conversation records.",
  },
];

function parseLeaderWorkspaceView(value: string | null): LeaderWorkspaceView | null {
  if (
    value === "review" ||
    value === "throughline" ||
    value === "patterns" ||
    value === "brief" ||
    value === "arc" ||
    value === "record"
  ) {
    return value;
  }
  return null;
}

function throughlineDayKeyFromPointId(pointId: string) {
  return pointId.startsWith("bucket-") ? pointId.replace("bucket-", "") : null;
}

function toCoachReviewResolvedSource(
  item: (EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null }) | null,
): CoachReviewResolvedSource | null {
  if (!item) return null;
  if (item.source === "Transcript") {
    return { source: "Transcript", itemId: item.id, meetingId: item.meeting_id };
  }
  if (item.subtype === "shared-reflection") {
    return { source: "Shared Reflection", itemId: item.id, meetingId: item.meeting_id };
  }
  if (item.subtype === "reflection" || item.source === "Reflection") {
    return { source: "Reflection", itemId: item.id, meetingId: item.meeting_id };
  }
  if (item.source === "Artifact") {
    return { source: "Artifact", itemId: item.id, meetingId: item.meeting_id };
  }
  return { source: "Evidence", itemId: item.id, meetingId: item.meeting_id };
}

const REVIEW_MATERIAL_SOURCE_LABELS = new Set([
  "Transcripts",
  "Reflections",
  "Artifacts",
  "Practice context",
  "Context graph",
  "Behavioral intelligence",
]);

const PAGE_OWNED_AUXILIARY_SOURCE_LABELS = new Set([
  "Billing",
  "Unread messages",
  "Meeting processing usage",
]);

function toLongitudinalAnalyticsInput(
  read: CoachLeaderLongitudinalRead,
  action: CoachLeaderLongitudinalAnalyticsAction,
): CoachLeaderLongitudinalAnalyticsInput {
  const base = {
    action,
    state: read.state,
    routeSurface: "coach_leader_detail",
    role: "coach",
  } as const;
  if (read.state === "insufficient_history") {
    return {
      ...base,
      aggregateSource: read.coverage.aggregateSource,
      exactSourceCoverage: read.coverage.exactSourceCoverage,
      authorizedSourceReadiness: read.coverage.authorizedSourceReadiness,
      supportingEpisodeCount: 0,
      challengingEpisodeCount: 0,
      graphEvidenceCount: read.coverage.graphEvidenceCount,
    };
  }
  if (
    read.state === "historical_signal"
    || read.state === "emerging_finding"
    || read.state === "evidence_backed_finding"
  ) {
    return {
      ...base,
      aggregateSource: read.coverage.aggregateSource,
      confidence: read.confidence,
      exactSourceCoverage: read.coverage.exactSourceCoverage,
      authorizedSourceReadiness: read.coverage.authorizedSourceReadiness,
      supportingEpisodeCount: read.supportingEpisodes.length,
      challengingEpisodeCount: read.challengingEpisodes.length,
      contradictionPresent: read.contradiction.present,
      contradictionCount: read.contradiction.count,
      contradictionContext: read.contradiction.context,
      graphEvidenceCount: read.coverage.graphEvidenceCount,
    };
  }
  return base;
}

function longitudinalImpressionKey(input: CoachLeaderLongitudinalAnalyticsInput): string {
  return JSON.stringify([
    input.state,
    input.aggregateSource ?? null,
    input.exactSourceCoverage ?? null,
    input.authorizedSourceReadiness ?? null,
    input.supportingEpisodeCount ?? null,
    input.challengingEpisodeCount ?? null,
    input.contradictionCount ?? null,
    input.graphEvidenceCount ?? null,
  ]);
}

type LeaderDetailPageContentProps = {
  user: ReturnType<typeof useAuth>["user"];
  authLoading: boolean;
  isDemoMode: boolean;
  isDemoLeader: boolean;
  leaderId?: string;
  relationshipAuthorization: ReturnType<typeof useCoachHealthDestinationAuthorization>;
  accessGeneration: string;
};

export function LeaderDetailPage() {
  const { user, loading: authLoading } = useAuth();
  const { isDemoMode } = useDemoMode();
  const { leaderId } = useParams();
  const isDemoLeader = Boolean(isDemoMode && leaderId?.startsWith("demo-"));
  const relationshipAuthorization = useCoachHealthDestinationAuthorization({
    coachId: user?.id,
    target: leaderId
      ? { kind: "relationship", leaderId }
      : null,
    enabled: Boolean(user?.id && leaderId && !isDemoLeader),
  });
  const accessGeneration = isDemoLeader
    ? "demo-authorized"
    : relationshipAuthorization.accessGeneration;
  const routeIdentityKey = `${user?.id ?? "anonymous"}:${isDemoMode ? "demo" : "live"}:${leaderId ?? "no-leader"}`;

  return (
    <CoachLayout>
      <LeaderDetailPageContent
        key={routeIdentityKey}
        user={user}
        authLoading={authLoading}
        isDemoMode={isDemoMode}
        isDemoLeader={isDemoLeader}
        leaderId={leaderId}
        relationshipAuthorization={relationshipAuthorization}
        accessGeneration={accessGeneration}
      />
    </CoachLayout>
  );
}

function LeaderDetailPageContent({
  user,
  authLoading,
  isDemoMode,
  isDemoLeader,
  leaderId,
  relationshipAuthorization,
  accessGeneration,
}: LeaderDetailPageContentProps) {
  const navigate = useNavigate();
  const { openPanel: openSharedCoachVeeraPanel } = useCoachVeeraPanel();
  const [searchParams, setSearchParams] = useSearchParams();
  const observationTelemetryKeyRef = useRef<string | null>(null);
  const coachVeeraCompatibilityAttemptRef = useRef<string | null>(null);
  const latestCoachVeeraCompatibilityIntentRef = useRef<string | null>(null);
  const {
    throughlineWindowDays,
    setThroughlineWindowDays,
    selectedThroughlineDay,
    setSelectedThroughlineDay,
  } = useThroughlineSelectionSync({ searchParams, setSearchParams });
  const [newNoteText, setNewNoteText] = useState("");
  const [carryForwardNoteText, setCarryForwardNoteText] = useState("");
  const [carryForwardVisibilityState, setCarryForwardVisibilityState] = useState<VisibilityState>("coach_only");
  const [carryForwardQuestionPreview, setCarryForwardQuestionPreview] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [selectedEvidenceSession, setSelectedEvidenceSession] = useState<SelectedEvidenceSession | null>(null);
  const [isEvidenceReviewDialogOpen, setIsEvidenceReviewDialogOpen] = useState(false);
  const [isReviewHubOpen, setIsReviewHubOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [, setAllReviewedAcknowledgedAt] = useState<string | null>(null);
  const [showBillingChoices, setShowBillingChoices] = useState(false);
  const [privateNotesOpen, setPrivateNotesOpen] = useState(false);
  const [agreementsOpen, setAgreementsOpen] = useState(false);
  const [agreementRecord, setAgreementRecord] = useState<AgreementRecord>({
    namedAgreements: [],
    conversationRecords: [],
  });
  const agreementSeedSignatureRef = useRef("");
  const handledJourneyActionRef = useRef<string | null>(null);
  const handledTranscriptReviewSectionRef = useRef<string | null>(null);
  const pendingWorkspaceScrollRef = useRef<{ view: LeaderWorkspaceView; sectionId: string } | null>(null);
  const { refetchNewEvidence } = useCoachNewEvidence();
  const queryClient = useQueryClient();

  const relationshipAuthorized = isDemoLeader || relationshipAuthorization.isAuthorized;
  const currentAccessGenerationRef = useRef(accessGeneration);
  currentAccessGenerationRef.current = accessGeneration;
  const relationshipReadEnabled = Boolean(
    !isDemoLeader && relationshipAuthorized,
  );
  const reauthorizeRelationshipAccess = relationshipAuthorization.reauthorizeAccess;
  const privateMutationTargetKey = JSON.stringify([
    user?.id ?? null,
    leaderId ?? null,
    isDemoLeader,
    relationshipAuthorization.relationshipRevision,
    accessGeneration,
  ]);
  const privateMutationGenerationRef = useRef(0);
  const latestPrivateMutationTargetKeyRef = useRef<string | null>(null);
  if (latestPrivateMutationTargetKeyRef.current !== privateMutationTargetKey) {
    latestPrivateMutationTargetKeyRef.current = privateMutationTargetKey;
    privateMutationGenerationRef.current += 1;
  }
  useLayoutEffect(() => {
    return () => {
      privateMutationGenerationRef.current += 1;
    };
  }, []);
  const capturePrivateMutationAttempt = useCallback(() => ({
    generation: privateMutationGenerationRef.current,
    targetKey: privateMutationTargetKey,
    relationshipRevision: relationshipAuthorization.relationshipRevision,
  }), [privateMutationTargetKey, relationshipAuthorization.relationshipRevision]);
  const isCurrentPrivateMutationAttempt = useCallback((attempt: {
    generation: number;
    targetKey: string;
    relationshipRevision: string | null;
  }) => (
    privateMutationGenerationRef.current === attempt.generation &&
    latestPrivateMutationTargetKeyRef.current === attempt.targetKey
  ), []);
  const reauthorizeCurrentPrivateMutation = useCallback(async (attempt: {
    generation: number;
    targetKey: string;
    relationshipRevision: string | null;
  }) => {
    if (isDemoLeader) return isCurrentPrivateMutationAttempt(attempt);
    const authorization = await reauthorizeRelationshipAccess();
    if (
      !authorization.authorized ||
      authorization.relationshipRevision !== attempt.relationshipRevision
    ) return false;
    return isCurrentPrivateMutationAttempt(attempt);
  }, [isCurrentPrivateMutationAttempt, isDemoLeader, reauthorizeRelationshipAccess]);
  const { data: portfolioAttentionSignals } = useCoachAttentionSignals({
    coachId: user?.id,
    isDemoMode,
    enabled: Boolean(user?.id) || isDemoMode,
  });

  useEffect(() => {
    if (!leaderId || !user?.id) return;
    setLastSeenAt(getCoachLeaderLastSeenAt(user.id, leaderId));
  }, [leaderId, user?.id]);

  const {
    notes,
    addNote,
    updateNote,
    deleteNote,
    isAdding,
    isUpdating,
    isDeleting,
    refetchNotes,
    error: notesError,
  } = useCoachNotes(leaderId || "", {
    enabled: relationshipReadEnabled,
    accessGeneration,
  });
  const {
    state: carryForwardState,
    reviewedCarryForward,
    loadError: carryForwardLoadError,
    isLoading: isLoadingCarryForward,
    refreshState: refreshCarryForwardState,
    saveState: saveCarryForwardState,
    isSaving: isSavingCarryForwardState,
  } = useCoachLeaderCarryForward(leaderId || "", {
    enabled: relationshipReadEnabled,
    accessGeneration,
    relationshipRevision: relationshipAuthorization.relationshipRevision,
  });

  useEffect(() => {
    if (!relationshipReadEnabled || !carryForwardState || carryForwardState.leader_id !== leaderId) {
      setCarryForwardNoteText("");
      setCarryForwardVisibilityState("coach_only");
      return;
    }
    setCarryForwardNoteText(carryForwardState.note);
    setCarryForwardVisibilityState(carryForwardState.visibility_state);
  }, [carryForwardState, leaderId, relationshipReadEnabled]);

  useEffect(() => {
    setNewNoteText("");
    setEditingNoteId(null);
    setEditingText("");
    if (!relationshipReadEnabled) {
      setCarryForwardNoteText("");
      setCarryForwardVisibilityState("coach_only");
    }
  }, [leaderId, relationshipReadEnabled]);

  const demoData = useMemo(() => {
    if (!isDemoLeader || !leaderId) return null;
    const leader = demoLeaders.find((l) => l.id === leaderId);
    if (!leader) return null;

    const transcript = demoTranscriptAnalysis.leaderId === leaderId ? demoTranscriptAnalysis : null;
      return {
        sourceReadiness: buildCoachLeaderDetailSourceReadiness({
          required: { relationship: "ready", profile: "ready" },
          optional: {
            transcripts: transcript ? "ready" : "empty",
            reflections: "empty",
            artifacts: "empty",
            practice: "empty",
            graph: "empty",
            intelligence: "empty",
            coachContext: "empty",
            billing: "empty",
            unreadMessages: "empty",
            processingUsage: "empty",
          },
        }),
        profile: {
        id: leader.id,
        full_name: leader.full_name,
        email: leader.email,
        coach_id: "demo-coach",
      },
      onboarding: null,
      transcripts: transcript
        ? [
            {
              id: `demo-transcript-${leaderId}`,
              created_at: transcript.analyzedAt,
              title: transcript.meetingTitle,
              summary: transcript.keyInsight,
              source: "transcript" as const,
              link: `/coach/post-meeting/demo-transcript-${leaderId}`,
              meeting_id: null,
              analysis_status: "analyzed",
              coach_verified: false,
            },
          ]
        : [],
        reflections: [],
        artifacts: [],
        practiceSummaries: [],
        practiceFocusSuggestions: [],
        meetingFeedback: [],
        ledgerEvents: [] as LeaderLedgerEventItem[],
        leaderMemoryInputs: [],
        latestCoachGrowthRecord: null,
        interventionMemory: null,
        coachLens: {
          coaching_philosophy: null,
          frameworks: null,
          voice_summary: null,
        },
        agreementRecord: {
          namedAgreements: [],
          conversationRecords: [],
        } as AgreementRecord,
        continuity: {
          topSummary: "Across the last few sessions, demo coaching focus keeps shifting while the most useful next practice carries forward and the next probe stays under review.",
          whatChanged: ["demo coaching focus"],
          whatCarriesForward: ["the most useful next practice"],
          whatToProbeNext: ["the next probe"],
          agreementRecord: {
            namedAgreements: [],
            conversationRecords: [],
          },
          latestCoachGrowthRecord: null,
          interventionMemory: null,
        },
        behavioralObservation: {
          source: null,
          created_at: null,
          moment: "Coach Veera needs more shared evidence before naming a stable pattern here.",
          pattern: null,
          reflection: null,
          isFallback: true,
        },
        longitudinalIntelligence: {
          aggregate: {
            primaryObservation: {
              behaviorKey: "demo_coaching_focus",
              label: "Illustrative coaching focus",
              source: "bi_v2" as const,
            },
            supportingSignals: [],
            confidence: "low" as const,
            contradictions: [],
            evidenceRefs: [],
            sourceSummary: {
              mode: "canonical_bi_v2" as const,
              usedCompatibilityFallback: false,
              thinEvidence: true,
              canonicalThemeCount: 1,
              graphEvidenceCount: 0,
            },
            supportSummary: {
              kind: "canonical" as const,
              artifactCount: transcript ? 1 : 0,
              meetingCount: transcript ? 1 : 0,
              thin: true,
            },
          },
          aggregateEvidenceRefs: [],
          aggregateReadiness: {
            canonical: "ready" as const,
            graph: "empty" as const,
            compatibility: "empty" as const,
            crosswalk: "empty" as const,
            legacy: "empty" as const,
          },
          authorizedSources: {
            status: "ready" as const,
            sources: [],
          },
          legacyEvidencePresent: false,
          observationWindowTruncated: false,
        },
      };
  }, [isDemoLeader, leaderId]);

  const {
    data,
    error: prepError,
    dataUpdatedAt: prepAssemblyUpdatedAt,
    errorUpdatedAt: prepAssemblyErrorUpdatedAt,
    isLoading,
    isFetching: isPrepFetching,
    refetch: refetchSessionPrep,
  } = useCoachSessionPrepData({
    coachId: user?.id,
    leaderId,
    accessGeneration,
    enabled: Boolean(
      leaderId && user && !isDemoLeader && relationshipAuthorized,
    ),
  });
  const coachLeaderSchedule = useCoachLeaderCallSchedule({
    coachId: user?.id,
    leaderId,
    relationshipRevision: relationshipAuthorization.relationshipRevision,
    accessGeneration,
    enabled: Boolean(!isDemoLeader && relationshipAuthorized),
  });
  const {
    viewedArtifactKeys,
    markTranscriptReviewOpened,
    markEvidenceReferenceViewed,
    markAllReviewed,
  } = useCoachReviewArtifactViews({
    coachId: user?.id,
    leaderId,
    enabled: Boolean(!isDemoLeader && relationshipAuthorized),
  });
  const { data: rawPracticeFocusReviewItems } = useCoachPracticeFocusReview({
    coachId: user?.id,
    leaderId,
    enabled: Boolean(!isDemoLeader && relationshipAuthorized),
  });
  const practiceFocusReviewItems = rawPracticeFocusReviewItems ?? [];
  const approvePracticeFocusSuggestion = useApproveCoachPracticeFocusSuggestion({
    coachId: user?.id,
    leaderId,
  });

  const resolvedData = isDemoLeader ? demoData : data;
  const unavailablePrepSourceLabels = useMemo(
    () => (resolvedData?.sourceReadiness?.unavailableSections ?? [])
      .filter((label) => !PAGE_OWNED_AUXILIARY_SOURCE_LABELS.has(label)),
    [resolvedData?.sourceReadiness?.unavailableSections],
  );
  const unavailablePrepSourceCopy = useMemo(() => {
    if (unavailablePrepSourceLabels.length === 0) return null;
    if (unavailablePrepSourceLabels.length === 1) {
      return `${unavailablePrepSourceLabels[0]} could not be loaded. The rest of this workspace is still available.`;
    }
    return `${unavailablePrepSourceLabels.join(", ")} could not be loaded. The rest of this workspace is still available.`;
  }, [unavailablePrepSourceLabels]);
  const { data: coachingRecordSummary } = useCoachingRecordSummary({
    coachId: user?.id,
    relationshipLeaderId: leaderId,
    relationshipLeaderName: resolvedData?.profile.full_name,
    enabled: Boolean(!isDemoLeader && relationshipAuthorized),
  });
  useContinuityMomentToast({
    userId: user?.id,
    summary: coachingRecordSummary,
    scopeId: leaderId,
    relationshipLeaderName: resolvedData?.profile.full_name,
    enabled: Boolean(!isDemoLeader && user?.id && leaderId && relationshipAuthorized),
  });
  const {
    coachLens: activeCoachLens,
    coachTwin: activeCoachTwin,
    coachMemory: activeCoachMemory,
  } = useCoachRuntimeContext({
    coachId: user?.id,
    leaderId,
    enabled: Boolean(user?.id && leaderId && !isDemoLeader && relationshipAuthorized),
  });

  // Sync agreement record from resolved data when it changes
  const resolvedAgreementRecordSignature = useMemo(
    () => agreementRecordSignature(resolvedData?.agreementRecord),
    [resolvedData?.agreementRecord],
  );

  useEffect(() => {
    if (!resolvedData?.agreementRecord) return;
    if (agreementSeedSignatureRef.current === resolvedAgreementRecordSignature) return;
    agreementSeedSignatureRef.current = resolvedAgreementRecordSignature;
    setAgreementRecord(resolvedData.agreementRecord);
  }, [resolvedAgreementRecordSignature, resolvedData?.agreementRecord]);

  // Fetch unread messages for this leader
  const {
    data: unreadMessageCount,
    error: unreadMessageError,
    refetch: refetchUnreadMessages,
  } = useQuery({
    queryKey: ["leader-unread-messages", user?.id, leaderId],
    enabled: Boolean(user?.id && leaderId && !isDemoLeader && relationshipAuthorized),
    queryFn: async () => {
      if (!user?.id || !leaderId) return 0;
      const { count, error: msgError } = await supabase
        .from("coach_messages")
        .select("*", { count: "exact", head: true })
        .eq("coach_id", user.id)
        .eq("leader_id", leaderId)
        .eq("read", false)
        .eq("sender_id", leaderId);
      if (msgError) throw msgError;
      return count || 0;
    },
  });

  const {
    data: leaderProcessingUsage,
    error: leaderProcessingUsageError,
    isFetching: isLeaderProcessingUsageFetching,
    refetch: refetchLeaderProcessingUsage,
  } = useQuery({
    queryKey: ["coach-leader-processing-usage", user?.id, leaderId],
    enabled: Boolean(user?.id && leaderId && !isDemoLeader && relationshipAuthorized),
    queryFn: async () => {
      if (!user?.id || !leaderId) {
        return {
          includedUsed: 0,
          extraCreditsRemaining: 0,
          includedLimit: COACH_LED_PROCESSED_MEETING_LIMIT,
          isLimitReached: false,
        };
      }

      const { data: link, error: linkError } = await supabase
        .from("coach_leader_links")
        .select("coach_id, leader_id, created_at, billing_started_at")
        .eq("coach_id", user.id)
        .eq("leader_id", leaderId)
        .maybeSingle();
      if (linkError) throw linkError;
      if (!link) {
        return {
          includedUsed: 0,
          extraCreditsRemaining: 0,
          includedLimit: COACH_LED_PROCESSED_MEETING_LIMIT,
          isLimitReached: false,
        };
      }

      const period = resolveLeaderBillingPeriod({
        anchor: link.billing_started_at ?? link.created_at,
      });

      let includedQuery = supabase
        .from("meeting_processing_events")
        .select("id")
        .eq("leader_id", leaderId)
        .eq("source", MEETING_PROCESSING_SOURCE_INCLUDED)
        .eq("event_type", MEETING_PROCESSING_EVENT_TYPE)
        .eq("billing_owner_type", "coach")
        .eq("billing_owner_id", user.id);

      if (period.periodStart && period.periodEnd) {
        includedQuery = includedQuery
          .eq("processing_period_start", period.periodStart)
          .eq("processing_period_end", period.periodEnd);
      }

      const [includedResult, extraEventResult, packsResult, subscriptionResult] = await Promise.all([
        includedQuery,
        supabase
          .from("meeting_processing_events")
          .select("id")
          .eq("leader_id", leaderId)
          .eq("source", MEETING_PROCESSING_SOURCE_EXTRA_PACK)
          .eq("event_type", MEETING_PROCESSING_EVENT_TYPE)
          .eq("billing_owner_type", "coach")
          .eq("billing_owner_id", user.id),
        supabase
          .from("meeting_processing_credit_packs")
          .select("id, quantity_purchased, quantity_used, status")
          .eq("leader_id", leaderId)
          .eq("purchased_by_type", "coach")
          .eq("purchased_by_id", user.id)
          .eq("status", "active"),
        supabase
          .from("subscriptions")
          .select("status, plan_type, promo_code_used")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      if (includedResult.error) throw includedResult.error;
      if (extraEventResult.error) throw extraEventResult.error;
      if (packsResult.error) throw packsResult.error;
      if (subscriptionResult.error) throw subscriptionResult.error;

      const includedUsed = includedResult.data?.length ?? 0;
      const extraCreditsRemaining = countRemainingExtraMeetingCredits(packsResult.data ?? []);
      const subscription = subscriptionResult.data as {
        status?: string | null;
        plan_type?: string | null;
        promo_code_used?: string | null;
      } | null;
      const includedLimit =
        subscription?.plan_type === COACH_ACTIVE_LEADER_PLAN_TYPE &&
        hasManualUnlimitedProcessingAccess({
          status: subscription.status,
          promoCodeUsed: subscription.promo_code_used,
        })
          ? null
          : COACH_LED_PROCESSED_MEETING_LIMIT;
      return {
        includedUsed,
        extraUsed: extraEventResult.data?.length ?? 0,
        extraCreditsRemaining,
        includedLimit,
        isLimitReached: includedLimit !== null && includedUsed >= includedLimit && extraCreditsRemaining <= 0,
      };
    },
  });

  const {
    data: pendingBillingResolution,
    error: pendingBillingResolutionError,
    isFetching: isPendingBillingResolutionFetching,
    refetch: refetchPendingBillingResolution,
  } = useQuery({
    queryKey: ["coach-leader-billing-resolution", user?.id, leaderId],
    enabled: Boolean(user?.id && leaderId && !isDemoLeader && relationshipAuthorized),
    queryFn: async () => {
      if (!user?.id || !leaderId) return null;
      const { data, error } = await supabase
        .from("billing_ownership_resolutions")
        .select("id, status, resolution_action, effective_at")
        .eq("leader_id", leaderId)
        .eq("proposed_owner_type", "coach")
        .eq("proposed_owner_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const unavailableAuxiliaryDetails = useMemo(() => [
    unreadMessageError ? "Unread messages" : null,
    leaderProcessingUsageError ? "meeting processing" : null,
  ].filter((label): label is string => Boolean(label)), [
    leaderProcessingUsageError,
    unreadMessageError,
  ]);
  const retryUnavailableAuxiliaryDetails = useCallback(() => {
    const retries: Array<Promise<unknown>> = [];
    if (unreadMessageError) retries.push(refetchUnreadMessages());
    if (leaderProcessingUsageError) retries.push(refetchLeaderProcessingUsage());
    return Promise.all(retries);
  }, [
    leaderProcessingUsageError,
    refetchLeaderProcessingUsage,
    refetchUnreadMessages,
    unreadMessageError,
  ]);
  // Auto-mark coach_alerts as read on visit
  useEffect(() => {
    if (!user?.id || !leaderId || isDemoLeader || !relationshipAuthorized) return;
    const attempt = capturePrivateMutationAttempt();
    const markAlertsRead = async () => {
      if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
      const { error: updateError } = await supabase
        .from("coach_alerts")
        .update({ is_read: true })
        .eq("leader_id", leaderId)
        .eq("coach_id", user.id)
        .eq("is_read", false);
      if (!updateError) {
        queryClient.invalidateQueries({ queryKey: ["coach-leaders-command"] });
        queryClient.invalidateQueries({ queryKey: ["coach-alerts"] });
      }
    };
    markAlertsRead();
  }, [capturePrivateMutationAttempt, isDemoLeader, leaderId, queryClient, reauthorizeCurrentPrivateMutation, relationshipAuthorized, user?.id]);

  const notesByDate = useMemo(() => {
    const grouped = new Map<string, typeof notes>();
    notes.forEach((note) => {
      const key = format(new Date(note.created_at), NOTE_DATE_FMT);
      const bucket = grouped.get(key) || [];
      bucket.push(note);
      grouped.set(key, bucket);
    });
    return Array.from(grouped.entries());
  }, [notes]);

  const selectedEvidence = selectedEvidenceSession?.evidence ?? null;

  const prepRuntime = useMemo(() => {
    return buildCoachSessionPrepRuntime({
      leaderId,
      prepData: resolvedData || null,
      notes,
      lastSeenAt,
      recentWindowDays: NEW_EVIDENCE_WINDOW_DAYS,
      agreementRecord,
      selectedEvidence,
      activeCoachLens,
      activeCoachTwin,
      activeCoachMemory,
      unreadMessageCount: unreadMessageCount ?? 0,
      windowOrigin: window.location.origin,
      isDemoLeader,
    });
  }, [
    activeCoachLens,
    activeCoachMemory,
    activeCoachTwin,
    agreementRecord,
    isDemoLeader,
    lastSeenAt,
    leaderId,
    notes,
    resolvedData,
    selectedEvidence,
    unreadMessageCount,
  ]);
  const portfolioIntelligence = useMemo(
    () => buildCoachPortfolioIntelligence({
      signals: portfolioAttentionSignals || [],
      currentLeaderId: leaderId || null,
    }),
    [leaderId, portfolioAttentionSignals],
  );

  const {
    pendingTranscripts,
    latestPendingTranscript,
    latestReviewedTranscript,
  } = prepRuntime.attention;
  const {
    evidenceItems,
    latestEvidence,
    sectionNewCounts,
    snapshot,
  } = prepRuntime.evidence;
  const meaningfulNoticingCards = prepRuntime.noticing;
  const meaningfulNoticingCardsWithViewState = useMemo(
    () => applyCoachNoticingViewState(meaningfulNoticingCards, evidenceItems, viewedArtifactKeys),
    [evidenceItems, meaningfulNoticingCards, viewedArtifactKeys],
  );
  const emergingPatternsSection = useMemo(() => {
    const cardsById = new Map(meaningfulNoticingCardsWithViewState.map((card) => [card.id, card]));
    return {
      ...prepRuntime.emergingPatterns,
      patterns: prepRuntime.emergingPatterns.patterns.map((pattern) => {
        const matchedCard = cardsById.get(pattern.id);
        return matchedCard
          ? {
              ...pattern,
              evidenceCount: matchedCard.evidence.length,
              evidence: matchedCard.evidence,
            }
          : pattern;
      }),
    };
  }, [meaningfulNoticingCardsWithViewState, prepRuntime.emergingPatterns]);
  const pendingTranscriptReviewItems = useMemo(
    () => pendingTranscripts.filter((transcript) => !viewedArtifactKeys.has(`transcript_review:${transcript.id}`)),
    [pendingTranscripts, viewedArtifactKeys],
  );
  const latestPendingTranscriptReview = pendingTranscriptReviewItems[0] || null;
  const unreviewedEvidenceQueue = useMemo(() => {
    const queue = new Map<string, {
      artifactKey: string;
      label: string;
      link: string | null;
      evidenceId?: string | null;
      resolvedSource: CoachReviewResolvedSource | null;
      matchedEvidence: (EvidenceTimelineItem & {
        subtype: string | null;
        meeting_id: string | null;
        canonicalIdentity: CoachEvidenceCanonicalIdentity;
      }) | null;
    }>();

    meaningfulNoticingCardsWithViewState.forEach((card) => {
      card.evidence.forEach((entry) => {
        if (entry.viewed) return;
        const matchedEvidence = resolveCoachEvidenceReference(evidenceItems, entry);
        const resolvedSource = toCoachReviewResolvedSource(matchedEvidence);
        if (resolvedSource?.source === "Transcript") return;
        const artifactKey = getCoachReviewArtifactKey({
          label: entry.label,
          link: entry.link,
          evidenceId: entry.evidenceId,
          resolvedSource,
        });

        if (!queue.has(artifactKey)) {
          queue.set(artifactKey, {
            artifactKey,
            label: entry.label,
            link: entry.link,
            evidenceId: entry.evidenceId,
            resolvedSource,
            matchedEvidence,
          });
        }
      });
    });

    return Array.from(queue.values());
  }, [evidenceItems, meaningfulNoticingCardsWithViewState]);
  const pendingEvidenceReviewCount = unreviewedEvidenceQueue.length;
  const newestPendingEvidenceAt = useMemo(() => {
    return unreviewedEvidenceQueue
      .map((item) => item.matchedEvidence?.created_at ?? null)
      .filter((value): value is string => Boolean(value) && Number.isFinite(Date.parse(value)))
      .sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
  }, [unreviewedEvidenceQueue]);
  const reviewMaterialSourceIssues = useMemo(() => (
    unavailablePrepSourceLabels
      .filter((label) => REVIEW_MATERIAL_SOURCE_LABELS.has(label))
  ), [unavailablePrepSourceLabels]);
  const contextualSourceLabels = useMemo(() => (
    unavailablePrepSourceLabels
      .filter((label) => !REVIEW_MATERIAL_SOURCE_LABELS.has(label))
  ), [unavailablePrepSourceLabels]);
  const decisionPresentation = buildLeaderDetailDecisionPresentation({
    scope: {
      coachId: isDemoLeader ? "demo-coach" : user?.id ?? "",
      leaderId: resolvedData?.profile.id ?? leaderId ?? "",
      relationshipRevision: isDemoLeader
        ? "demo-revision"
        : relationshipAuthorization.relationshipRevision ?? "",
      accessGeneration,
      mode: isDemoLeader ? "demo" : "live",
    },
    access: isDemoLeader || relationshipAuthorization.isAuthorized
      ? { status: "authorized" }
      : relationshipAuthorization.isChecking
        ? { status: "checking" }
        : { status: "unavailable" },
    billing: isDemoLeader
      ? { status: "ready", value: { pendingResolution: false, processingLimitReached: false } }
      : pendingBillingResolutionError
        ? { status: "error" }
        : (pendingBillingResolution === undefined && isPendingBillingResolutionFetching)
          || (leaderProcessingUsage === undefined && isLeaderProcessingUsageFetching)
          ? { status: "loading" }
          : {
              status: "ready",
              value: {
                pendingResolution: Boolean(pendingBillingResolution),
                processingLimitReached: Boolean(leaderProcessingUsage?.isLimitReached),
              },
            },
    workspace: prepError && !resolvedData
      ? { status: "error" }
      : isLoading
        ? { status: "loading" }
        : { status: "ready", value: { ready: true } },
    review: reviewMaterialSourceIssues.length > 0
      ? { status: "error" }
      : {
          status: "ready",
          value: {
            transcripts: pendingTranscriptReviewItems.map((transcript) => ({
              id: transcript.id,
              title: transcript.title,
              createdAt: transcript.created_at,
              requiresProcessing: false,
            })),
            pendingEvidenceCount: pendingEvidenceReviewCount,
            newestEvidenceAt: newestPendingEvidenceAt,
          },
        },
    upcoming: isDemoLeader
      ? { status: "ready", value: { meetings: [] } }
      : coachLeaderSchedule.isPending
        ? { status: "loading" }
        : coachLeaderSchedule.isError || !coachLeaderSchedule.data
          ? { status: "error" }
          : { status: "ready", value: { meetings: coachLeaderSchedule.data.meetings } },
    context: isDemoLeader
      ? { status: "ready", value: { missing: { kind: "no_upcoming_meeting" } } }
      : coachLeaderSchedule.isError
        ? { status: "error" }
        : {
            status: "ready",
            value: {
              missing: coachLeaderSchedule.data?.meetings.length === 0
                ? { kind: "no_upcoming_meeting" }
                : null,
            },
          },
    sourceReadiness: prepError
      ? { status: "error" }
      : {
          status: "ready",
          value: {
            unavailableLabels: contextualSourceLabels,
            affectsPrimaryAction: contextualSourceLabels.length > 0,
          },
        },
    auxiliaryReadiness: {
      status: "ready",
      value: { unavailableLabels: unavailableAuxiliaryDetails },
    },
    now: isDemoLeader
      ? "2026-07-18T12:00:00.000Z"
      : coachLeaderSchedule.data?.observedAt ?? "2026-07-18T12:00:00.000Z",
  });
  const leaderDetailSourceReadiness = useMemo(() => {
    const baseSources = resolvedData?.sourceReadiness?.sources;
    if (!baseSources) return null;
    return buildCoachLeaderDetailSourceReadiness({
      required: {
        relationship: relationshipAuthorized ? "ready" : "revoked",
        profile: baseSources.profile,
      },
      optional: {
        transcripts: baseSources.transcripts,
        reflections: baseSources.reflections,
        artifacts: baseSources.artifacts,
        practice: baseSources.practice,
        graph: baseSources.graph,
        intelligence: baseSources.intelligence,
        coachContext: baseSources.coachContext,
        billing: pendingBillingResolutionError
          ? "unknown"
          : pendingBillingResolution === undefined
            ? "unknown"
            : pendingBillingResolution
              ? "ready"
              : "empty",
        unreadMessages: unreadMessageError
          ? "unknown"
          : typeof unreadMessageCount === "number"
            ? unreadMessageCount > 0 ? "ready" : "empty"
            : "unknown",
        processingUsage: leaderProcessingUsageError
          ? "unknown"
          : leaderProcessingUsage
            ? "ready"
            : "unknown",
      },
    });
  }, [
    leaderProcessingUsage,
    leaderProcessingUsageError,
    pendingBillingResolution,
    pendingBillingResolutionError,
    relationshipAuthorized,
    resolvedData?.sourceReadiness?.sources,
    unreadMessageCount,
    unreadMessageError,
  ]);
  const nextLeaderMeeting = useMemo(() => {
    const observedAt = Date.parse(coachLeaderSchedule.data?.observedAt ?? "");
    if (!Number.isFinite(observedAt)) return null;
    const match = [...(coachLeaderSchedule.data?.meetings ?? [])]
      .filter((meeting) => meeting.status === "scheduled" && Date.parse(meeting.startsAt) >= observedAt)
      .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt))[0];
    if (!match) return null;
    return {
      canonicalSessionId: match.coachCallId,
      startsAt: match.startsAt,
      title: match.title,
    };
  }, [coachLeaderSchedule.data]);
  const leaderDetailRecentContext = useMemo(() => {
    const items: CoachLeaderDetailRecentContextFact[] = [];
    const findExactEvidence = (
      sourceType: CoachEvidenceCanonicalIdentity["sourceType"],
      sourceId: string,
    ) => evidenceItems.find((item) => (
      item.canonicalIdentity.sourceType === sourceType
      && item.canonicalIdentity.sourceId === sourceId
      && item.canonicalIdentity.leaderId === leaderId
    )) ?? null;

    (resolvedData?.transcripts ?? []).forEach((transcript) => {
      const summary = transcript.summary?.trim();
      if (!transcript.id || !transcript.created_at || !transcript.title || !summary) return;
      const evidence = findExactEvidence("transcript_upload", transcript.id);
      if (!evidence) return;
      items.push({
        canonicalSourceId: transcript.id,
        sourceType: "transcript",
        occurredAt: transcript.created_at,
        occurredAtLabel: "Transcript added",
        title: transcript.title,
        summary,
        usable: true,
        evidenceReference: { evidenceId: evidence.id, link: evidence.link },
      });
    });
    (resolvedData?.reflections ?? []).forEach((reflection) => {
      const summary = reflection.text?.trim();
      if (!reflection.id || !reflection.created_at || !reflection.title || !summary) return;
      const canonicalSourceId = reflection.canonical_source_id || reflection.id;
      const canonicalSourceType = reflection.source === "assessment_reflection"
        ? "assessment"
        : reflection.source === "shared_reflection"
          ? "shared_reflection"
          : "post_meeting_reflection";
      const evidence = findExactEvidence(canonicalSourceType, canonicalSourceId);
      if (!evidence) return;
      items.push({
        canonicalSourceId,
        sourceType: reflection.source === "assessment_reflection" ? "assessment" : "reflection",
        occurredAt: reflection.created_at,
        occurredAtLabel: "Reflection added",
        title: reflection.title,
        summary,
        usable: true,
        evidenceReference: { evidenceId: evidence.id, link: evidence.link },
      });
    });
    (resolvedData?.artifacts ?? []).forEach((artifact) => {
      if (!artifact.id || !artifact.created_at || !artifact.title) return;
      const evidence = findExactEvidence("leader_artifact", artifact.id);
      if (!evidence) return;
      items.push({
        canonicalSourceId: artifact.id,
        sourceType: "artifact",
        occurredAt: artifact.created_at,
        occurredAtLabel: "Artifact added",
        title: artifact.title,
        summary: `Saved ${artifact.type || "leader"} context.`,
        usable: true,
        evidenceReference: { evidenceId: evidence.id, link: evidence.link },
      });
    });
    return items;
  }, [evidenceItems, leaderId, resolvedData?.artifacts, resolvedData?.reflections, resolvedData?.transcripts]);
  const reviewReadyPresentationEvidence = useMemo(() => {
    if (latestPendingTranscriptReview) {
      const transcript = leaderDetailRecentContext.find((item) => (
        item.sourceType === "transcript"
        && item.canonicalSourceId === latestPendingTranscriptReview.id
      ));
      if (transcript) return transcript;
    }
    const exactPending = unreviewedEvidenceQueue.find((item) => item.matchedEvidence)?.matchedEvidence;
    if (!exactPending || !("canonicalIdentity" in exactPending)) return null;
    return leaderDetailRecentContext.find((item) => (
      item.canonicalSourceId === exactPending.canonicalIdentity.sourceId
    )) ?? null;
  }, [latestPendingTranscriptReview, leaderDetailRecentContext, unreviewedEvidenceQueue]);
  const leaderDetailPresentation = useMemo(() => adaptLeaderDetailDecisionPresentation({
    decision: decisionPresentation,
    recentContext: leaderDetailRecentContext,
    reviewReadyEvidence: reviewReadyPresentationEvidence,
    unavailableSections: [
      ...(leaderDetailSourceReadiness?.unavailableSections ?? []),
      ...(notesError ? ["Private notes"] : []),
    ],
  }), [
    decisionPresentation,
    leaderDetailRecentContext,
    leaderDetailSourceReadiness?.unavailableSections,
    notesError,
    reviewReadyPresentationEvidence,
  ]);
  const longitudinalRead = useMemo(() => {
    if (isLoading) return buildCoachLeaderLongitudinalRead({ status: "loading" });
    if (!relationshipAuthorized) return buildCoachLeaderLongitudinalRead({ status: "revoked" });
    if (prepError || !resolvedData?.longitudinalIntelligence) {
      return buildCoachLeaderLongitudinalRead({ status: "unavailable", retryable: true });
    }

    const readiness = resolvedData.longitudinalIntelligence.aggregateReadiness;
    const canonicalUnknown = readiness.canonical === "retryable_error";
    const fallbackUnknown = readiness.canonical === "empty"
      && readiness.compatibility === "retryable_error";
    if (canonicalUnknown || fallbackUnknown) {
      return buildCoachLeaderLongitudinalRead({ status: "unavailable", retryable: true });
    }

    const aggregate = readiness.graph === "retryable_error"
      ? {
          ...resolvedData.longitudinalIntelligence.aggregate,
          sourceSummary: {
            ...resolvedData.longitudinalIntelligence.aggregate.sourceSummary,
            graphEvidenceCount: null,
          },
        }
      : resolvedData.longitudinalIntelligence.aggregate;

    return buildCoachLeaderLongitudinalRead({
      status: "ready",
      aggregate,
      aggregateEvidenceRefs: resolvedData.longitudinalIntelligence.aggregateEvidenceRefs,
      authorizedSources: resolvedData.longitudinalIntelligence.authorizedSources,
      legacyEvidencePresent: resolvedData.longitudinalIntelligence.legacyEvidencePresent,
      observationWindowTruncated: resolvedData.longitudinalIntelligence.observationWindowTruncated,
    });
  }, [
    isLoading,
    prepError,
    relationshipAuthorized,
    resolvedData?.longitudinalIntelligence,
  ]);
  const capturedLongitudinalImpressionsRef = useRef(new Set<string>());
  const captureLongitudinalAnalytics = useCallback((action: CoachLeaderLongitudinalAnalyticsAction) => {
    try {
      capturePostHogEvent(
        COACH_LEADER_LONGITUDINAL_EVENTS[action],
        buildCoachLeaderLongitudinalAnalytics(toLongitudinalAnalyticsInput(longitudinalRead, action)),
      );
    } catch {
      // Analytics is non-critical and must never alter the authorized product action.
    }
  }, [longitudinalRead]);
  const currentLongitudinalImpressionKey = useMemo(
    () => longitudinalImpressionKey(toLongitudinalAnalyticsInput(longitudinalRead, "rendered")),
    [longitudinalRead],
  );
  useEffect(() => {
    if (!resolvedData || relationshipAuthorization.isUnavailable || longitudinalRead.state === "revoked") return;
    if (capturedLongitudinalImpressionsRef.current.has(currentLongitudinalImpressionKey)) return;
    capturedLongitudinalImpressionsRef.current.add(currentLongitudinalImpressionKey);
    captureLongitudinalAnalytics("rendered");
  }, [
    captureLongitudinalAnalytics,
    currentLongitudinalImpressionKey,
    longitudinalRead.state,
    relationshipAuthorization.isUnavailable,
    resolvedData,
  ]);
  const throughlinePresentation = useThroughlinePresentation({
    data: prepRuntime.throughline.data,
    historyItems: prepRuntime.throughline.historyItems,
    recentConversationRecords: prepRuntime.throughline.recentConversationRecords,
    agreementLines: [],
    throughlineWindowDays,
    selectedThroughlineDay,
  });
  const {
    throughlineArcPoints,
    throughlineGapAlert,
    sparseWindowAutoExpanded,
    throughlineStateCounts,
    chartLabelPoints,
    throughlineDayRollups,
    selectedThroughlineRollup,
  } = throughlinePresentation;

  const throughlineUnsaidSignals = useMemo(() => {
    return buildCoachSessionPrepUnsaidSignals(selectedThroughlineRollup);
  }, [selectedThroughlineRollup]);
  const selectedThroughlineDayViewModel = useMemo(() => {
    return buildCoachThroughlineSelectedDay({
      selectedThroughlineRollup,
      throughlineUnsaidSignals,
      throughlineGapAlert,
    });
  }, [selectedThroughlineRollup, throughlineUnsaidSignals, throughlineGapAlert]);

  useEffect(() => {
    if (!throughlineArcPoints.length) {
      setSelectedThroughlineDay(null);
      return;
    }
    const existingSelectionInScope = selectedThroughlineDay && throughlineDayRollups.has(selectedThroughlineDay);
    if (existingSelectionInScope) return;
    const preferred =
      [...throughlineArcPoints].reverse().find((point) => !point.isEmpty)?.id
      || throughlineArcPoints[throughlineArcPoints.length - 1].id;
    setSelectedThroughlineDay(throughlineDayKeyFromPointId(preferred));
  }, [selectedThroughlineDay, setSelectedThroughlineDay, throughlineArcPoints, throughlineDayRollups]);

  const handleReviewLatestEvidence = () => {
    if (unreviewedEvidenceQueue.length === 0) {
      toast.info("Everything in this evidence set is already reviewed.");
      return;
    }
    setIsEvidenceReviewDialogOpen(true);
  };

  const openSelectedEvidence = useCallback((
    evidence: EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null },
    options?: { returnToQueue?: boolean },
  ) => {
    setIsReviewHubOpen(false);
    setIsEvidenceReviewDialogOpen(false);
    setSelectedEvidenceSession({
      evidence,
      returnToQueue: Boolean(options?.returnToQueue),
    });
  }, []);

  const closeSelectedEvidence = useCallback(() => {
    const shouldReturnToQueue = selectedEvidenceSession?.returnToQueue === true;
    setSelectedEvidenceSession(null);
    if (shouldReturnToQueue) {
      setIsEvidenceReviewDialogOpen(true);
    }
  }, [selectedEvidenceSession]);

  const handleOpenEvidenceReference = async (reference: {
    label: string;
    link: string | null;
    evidenceId?: string | null;
    sourceRevision?: string;
    source?: "coach_veera_answer" | "coach_veera_brief" | "coach_veera_claim";
    returnToQueue?: boolean;
  }) => {
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    const { label, link, evidenceId, source, returnToQueue } = reference;
    const match = resolveCoachEvidenceReference(evidenceItems, { label, link, evidenceId });
    if (!isCurrentPrivateMutationAttempt(attempt)) return;

    if (reference.sourceRevision) {
      if (!match || !user?.id || !leaderId) { toast.error("Refresh session prep before opening this source.");return; }
      const checked=await authorizeCoachEvidenceReference({coachId:user.id,leaderId,
        relationshipRevision:relationshipAuthorization.relationshipRevision,resolvedItem:match,
        expectedSourceRevision:reference.sourceRevision,expectedAccessGeneration:accessGeneration,
        getCurrentAccessGeneration:()=>currentAccessGenerationRef.current,reauthorizeRelationship:relationshipAuthorization.reauthorizeAccess});
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      if (checked.status!=="authorized") { toast.error("Source changed or unavailable. Refresh session prep before reviewing it.");return; }
    }
    if (match) {
      if (user?.id && leaderId && source) {
        await trackCoachVeeraEvidenceOpened({
          coachId: user.id,
          leaderId,
          surface: "coach_session_prep",
          evidenceSource: source,
          label,
          route: link,
        });
      }
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      await markEvidenceReferenceViewed({
        reference: { label, link, evidenceId },
        resolvedSource: toCoachReviewResolvedSource(match),
        openedFrom: "meaningful-noticing",
      });
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      openSelectedEvidence(match, { returnToQueue });
      return;
    }

    const fallbackItem = {
      id: `reference-${Date.now()}`,
      created_at: null,
      title: label,
      detail: "Source reference is available, but a detailed summary is not indexed yet.",
      link: toCoachEvidenceLink(link),
      source: "Evidence",
      subtype: null,
      meeting_id: null,
      availability: "not_indexed" as const,
    } satisfies EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null };
    openSelectedEvidence(fallbackItem, { returnToQueue });
  };

  const handleThroughlineNodeSelect = (dayKey: string) => {
    setSelectedThroughlineDay(dayKey);
  };

  const handleThroughlineSourceOpen = (item: { label: string; link?: string | null }) => {
    handleOpenEvidenceReference({ label: item.label, link: item.link || null });
  };

  const handleThroughlineOpenAllSources = async () => {
    if (!selectedThroughlineRollup) return;
    const allItems = [
      ...selectedThroughlineRollup.itemsByType.transcript,
      ...selectedThroughlineRollup.itemsByType.reflection,
      ...selectedThroughlineRollup.itemsByType.conversation,
      ...selectedThroughlineRollup.itemsByType.veera,
    ];
    if (allItems.length === 0) return;
    const direct = allItems.find((item) => item.link);
    if (direct) {
      handleThroughlineSourceOpen(direct);
      return;
    }
    const fallback = allItems[0];
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    openSelectedEvidence({
      id: `throughline-${fallback.id}`,
      created_at: fallback.at,
      title: fallback.label,
      detail: fallback.detail,
      link: null,
      source: "Evidence",
      subtype: null,
      meeting_id: null,
    });
  };

  const handleAskVeeraAboutSelectedDay = async () => {
    if (!selectedThroughlineRollup) {
      await handleOpenSharedCoachVeeraPanel(
        null,
        "leader_detail_throughline",
        "What changed since the last coach check-in, and what should I probe first?",
      );
      return;
    }
    const questionSeed = `For ${selectedThroughlineRollup.label}, what changed, what is unsaid, and what should I probe in the next 15 minutes?`;
    await handleOpenSharedCoachVeeraPanel(null, "leader_detail_throughline", questionSeed);
  };

  const scrollToSection = useCallback((sectionKey: string) => {
    const sectionId = LEGACY_SECTION_ALIASES[sectionKey]
      ?? (sectionKey.startsWith("section-") ? sectionKey : `section-${sectionKey}`);
    const section = document.getElementById(sectionId);
    if (!section) return;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleMarkSeen = async () => {
    if (!leaderId || !user?.id) return;
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    const wasMarked = await markAllReviewed({
      transcriptIds: pendingTranscriptReviewItems.map((transcript) => transcript.id),
      evidenceReferences: unreviewedEvidenceQueue.map((item) => ({
        label: item.label,
        link: item.link,
        evidenceId: item.evidenceId,
        resolvedSource: item.resolvedSource,
      })),
      openedFrom: "leader-detail-bulk-review",
      extraInvalidationKeys: [
        ["coach-leaders-command", user.id, isDemoLeader],
        ["coach-pending-transcript-review-count", user.id],
      ],
    });
    if (!wasMarked) return;
    if (!isCurrentPrivateMutationAttempt(attempt)) return;

    const seenAt = markCoachLeaderSeenNow(user.id, leaderId);
    setLastSeenAt(seenAt);
    setAllReviewedAcknowledgedAt(seenAt);
    void refetchNewEvidence();
  };

  const handleApprovePracticeFocus = async (item: CoachPracticeFocusReviewItem) => {
    const leaderSafeEvidence = item.evidence.filter((evidence) => evidence.leaderSafeSummary);
    if (leaderSafeEvidence.length === 0) {
      toast.error("Practice Focus needs a leader-safe evidence summary before it can be shared.");
      return;
    }
    const attempt = capturePrivateMutationAttempt();
    try {
      if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
      await approvePracticeFocusSuggestion.mutateAsync({
        suggestionId: item.id,
        evidenceSummaries: leaderSafeEvidence.map((evidence) => ({
          evidenceId: evidence.id,
          leaderSafeSummary: evidence.leaderSafeSummary || "",
        })),
      });
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      const prepReadback = await refetchSessionPrep();
      if (prepReadback.error) throw prepReadback.error;
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.success("Practice Focus approved for the leader.");
    } catch (approvalError) {
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.error(toDisplayError(approvalError));
    }
  };

  const handleSaveNote = async () => {
    const trimmed = newNoteText.trim();
    if (!trimmed || !leaderId) return;
    const attempt = capturePrivateMutationAttempt();
    try {
      if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
      const writeResult = await addNote(trimmed);
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      if (writeResult?.status === "saved_readback_failed") {
        toast.warning("Private note saved, but the notes list could not refresh.", {
          action: {
            label: "Retry refresh",
            onClick: () => { void refetchNotes(); },
          },
        });
        return;
      }
      setNewNoteText("");
      toast.success("Private note saved");
    } catch (noteError) {
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.error(`Private note was not saved. ${toDisplayError(noteError)}`);
    }
  };

  const handleSaveCarryForwardNote = async () => {
    const trimmed = carryForwardNoteText.trim();
    if (!trimmed || !leaderId) return;
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    await saveCarryForwardState({
      note: trimmed,
      visibilityState: carryForwardVisibilityState,
    });
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    setCarryForwardNoteText(trimmed);
    toast.success("Carry-forward note saved");
  };

  const handlePreviewCarryForwardQuestion = useCallback(async (question: string) => {
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    if (!mergeCarryForwardQuestion(carryForwardNoteText, question)) {
      toast.info("That question is already in the private carry-forward note.");
      return;
    }
    setCarryForwardQuestionPreview(question);
  }, [capturePrivateMutationAttempt, carryForwardNoteText, reauthorizeCurrentPrivateMutation]);

  const handleSaveCarryForwardQuestion = useCallback(async () => {
    if (!carryForwardQuestionPreview || !leaderId) return;
    const mergedNote = mergeCarryForwardQuestion(carryForwardNoteText, carryForwardQuestionPreview);
    if (!mergedNote) {
      setCarryForwardQuestionPreview(null);
      toast.info("That question is already in the private carry-forward note.");
      return;
    }
    const attempt = capturePrivateMutationAttempt();
    try {
      if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
      const saved = await saveCarryForwardState({
        note: mergedNote,
        visibilityState: carryForwardVisibilityState,
        expectedUpdatedAt: carryForwardState?.updated_at ?? null,
      });
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      if (saved.leader_id !== leaderId || saved.note !== mergedNote) {
        toast.error("The carry-forward note could not be confirmed. Please try again.");
        return;
      }
      setCarryForwardNoteText(saved.note);
      setCarryForwardVisibilityState(saved.visibility_state);
      setCarryForwardQuestionPreview(null);
      toast.success("Question added to the private carry-forward note.");
      captureLongitudinalAnalytics("questionAdded");
    } catch (error) {
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      await refreshCarryForwardState();
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.error(error instanceof Error && /changed/i.test(error.message)
        ? "This private note changed elsewhere. Review the latest note before saving again."
        : `The carry-forward note was not saved. ${toDisplayError(error)}`);
    }
  }, [
    capturePrivateMutationAttempt,
    carryForwardNoteText,
    carryForwardQuestionPreview,
    carryForwardState?.updated_at,
    carryForwardVisibilityState,
    captureLongitudinalAnalytics,
    isCurrentPrivateMutationAttempt,
    leaderId,
    reauthorizeCurrentPrivateMutation,
    refreshCarryForwardState,
    saveCarryForwardState,
  ]);

  const startEditing = (noteId: string, content: string) => {
    setEditingNoteId(noteId);
    setEditingText(content);
  };

  const handleSaveEdit = async () => {
    if (!editingNoteId) return;
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    await updateNote({ id: editingNoteId, content: editingText });
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    setEditingNoteId(null);
    setEditingText("");
  };

  const handleDeleteNote = async (noteId: string) => {
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    await deleteNote(noteId);
  };

  const handleResolveBillingOwnership = async (
    resolutionAction: typeof KEEP_SOLO_BILLING | typeof TRANSFER_TO_COACH | typeof DEFER_BILLING_TRANSFER,
  ) => {
    if (!leaderId) return;
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return;
    try {
      const { error: billingError } = await supabase.functions.invoke("billing-ownership", {
        body: {
          leader_id: leaderId,
          resolution_action: resolutionAction,
        },
      });
      if (billingError) throw billingError;
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.success(
        resolutionAction === DEFER_BILLING_TRANSFER
          ? "Billing decision deferred."
          : "Billing ownership updated.",
      );
      void refetchPendingBillingResolution();
      void refetchLeaderProcessingUsage();
    } catch (billingError) {
      if (!isCurrentPrivateMutationAttempt(attempt)) return;
      toast.error(toDisplayError(billingError));
    }
  };

  const sectionParam = searchParams.get("section");
  const tabParam = searchParams.get("tab");
  const actionParam = searchParams.get("action");
  const extraPackParam = searchParams.get("extra_pack");
  const evidenceTypeParam = searchParams.get("type");
  const evidenceMeetingIdParam = searchParams.get("meetingId");
  const evidenceIdParam = searchParams.get("id");
  const sourceTranscriptIdParam = searchParams.get("sourceTranscriptId");
  const sharedSessionParam = searchParams.get("session");
  const workspaceView = parseLeaderWorkspaceView(searchParams.get("workspace"));
  const coachVeeraCompatibilityIntentKey = `${accessGeneration}:${resolvedData?.profile.id ?? leaderId ?? "no-leader"}:${sectionParam === "veera" ? "veera" : "none"}`;
  latestCoachVeeraCompatibilityIntentRef.current = coachVeeraCompatibilityIntentKey;
  const forceDetailForLegacyDeepLink = !workspaceView && Boolean(sectionParam || tabParam || actionParam);
  const showWorkspaceMap = !workspaceView && !forceDetailForLegacyDeepLink;
  const showReviewWorkspace = workspaceView === "review" || forceDetailForLegacyDeepLink;
  const showThroughlineWorkspace = workspaceView === "throughline" || forceDetailForLegacyDeepLink;
  const showPatternsWorkspace = workspaceView === "patterns" || forceDetailForLegacyDeepLink;
  const showBriefWorkspace = workspaceView === "brief" || forceDetailForLegacyDeepLink;
  const showArcWorkspace = workspaceView === "arc" || forceDetailForLegacyDeepLink;
  const showRecordWorkspace = workspaceView === "record" || forceDetailForLegacyDeepLink;
  const selectedWorkspaceCopy = workspaceView ? LEADER_WORKSPACE_LABELS[workspaceView] : null;
  const isWorkspaceDetailView = !showWorkspaceMap;
  const isFocusedWorkspaceView = workspaceView === "brief" || workspaceView === "throughline" || workspaceView === "record";
  const agreementsAvailable = !leaderDetailSourceReadiness
    || ["ready", "empty"].includes(leaderDetailSourceReadiness.sources.coachContext);

  const openWorkspace = useCallback((view: LeaderWorkspaceView) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("workspace", view);
      next.delete("section");
      return next;
    });
  }, [setSearchParams]);

  const handlePageBack = useCallback(() => {
    if (isFocusedWorkspaceView) {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("workspace");
        next.delete("section");
        return next;
      });
      return;
    }

    if (isWorkspaceDetailView) {
      navigate(`/coach/leaders/${leaderId}`);
      return;
    }

    navigate("/coach/leaders");
  }, [isFocusedWorkspaceView, isWorkspaceDetailView, leaderId, navigate, setSearchParams]);

  const openWorkspaceAndScroll = useCallback((view: LeaderWorkspaceView, sectionId: string) => {
    pendingWorkspaceScrollRef.current = { view, sectionId };
    openWorkspace(view);
  }, [openWorkspace]);

  const scrollWithinWorkspace = useCallback((view: LeaderWorkspaceView, sectionId: string) => {
    if (workspaceView === view || forceDetailForLegacyDeepLink) {
      scrollToSection(sectionId);
      return;
    }

    openWorkspaceAndScroll(view, sectionId);
  }, [forceDetailForLegacyDeepLink, openWorkspaceAndScroll, scrollToSection, workspaceView]);

  const handleBrowseEvidence = useCallback(() => {
    scrollWithinWorkspace("throughline", "section-noticing");
  }, [scrollWithinWorkspace]);

  useEffect(() => {
    const pending = pendingWorkspaceScrollRef.current;
    if (!pending || workspaceView !== pending.view || isLoading || !resolvedData) return undefined;

    pendingWorkspaceScrollRef.current = null;
    const timeoutId = window.setTimeout(() => {
      scrollToSection(pending.sectionId);
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, resolvedData, scrollToSection, workspaceView]);

  useEffect(() => {
    if (window.navigator.userAgent.includes("jsdom")) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      try {
        window.scrollTo({ top: 0, left: 0 });
      } catch {
        // Some test environments expose scrollTo without implementing it.
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [workspaceView]);

  const deepLinkedEvidenceItems = useMemo(() => {
    return evidenceItems.filter((item) => {
      if (evidenceTypeParam === "transcript" && item.source !== "Transcript") return false;
      if (evidenceTypeParam === "reflection" && item.subtype !== "reflection") return false;
      if (evidenceTypeParam === "shared-reflection" && item.subtype !== "shared-reflection") return false;
      if (evidenceTypeParam === "artifact" && item.source !== "Artifact") return false;
      if (evidenceMeetingIdParam && item.meeting_id !== evidenceMeetingIdParam) return false;
      if (evidenceIdParam && !item.id.includes(evidenceIdParam)) return false;
      return true;
    });
  }, [evidenceIdParam, evidenceItems, evidenceMeetingIdParam, evidenceTypeParam]);

  useEffect(() => {
    if (!extraPackParam || !resolvedData?.profile.full_name) return;
    if (extraPackParam === "success") {
      toast.success(`5 additional meeting processes have been added for ${resolvedData.profile.full_name}.`);
      void refetchLeaderProcessingUsage();
    } else if (extraPackParam === "canceled") {
      toast.info("No additional meeting processes were added.");
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("extra_pack");
      return next;
    }, { replace: true });
  }, [extraPackParam, refetchLeaderProcessingUsage, resolvedData?.profile.full_name, setSearchParams]);

  useEffect(() => {
    if (isLoading || !resolvedData) return;
    if (tabParam !== "journey") return;
    scrollToSection("journey");
  }, [isLoading, resolvedData, scrollToSection, tabParam]);

  useEffect(() => {
    if (isLoading || !resolvedData) return;
    if (sectionParam !== "agreements" || (!sourceTranscriptIdParam && !sharedSessionParam)) return;
    setAgreementsOpen(true);
    if (sharedSessionParam && (searchParams.get("step") || searchParams.get("response"))) return;
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(sharedSessionParam ? "selected-shared-session" : `agreement-transcript-${sourceTranscriptIdParam}`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timeoutId);
  }, [isLoading, resolvedData, sectionParam, sourceTranscriptIdParam, sharedSessionParam, searchParams]);

  useEffect(() => {
    if (isLoading || !resolvedData) return;
    if (sectionParam !== "transcript-review") return;
    scrollToSection("transcript-review");
    if (!latestPendingTranscriptReview?.id) return;
    const reviewKey = `${leaderId || "no-leader"}:${latestPendingTranscriptReview.id}:${sectionParam}`;
    if (handledTranscriptReviewSectionRef.current === reviewKey) return;
    handledTranscriptReviewSectionRef.current = reviewKey;
    const attempt = capturePrivateMutationAttempt();
    void reauthorizeCurrentPrivateMutation(attempt).then((authorized) => {
      if (!authorized) return;
      return markTranscriptReviewOpened({
        transcriptId: latestPendingTranscriptReview.id,
        openedFrom: "leader-transcript-review-section",
        extraInvalidationKeys: [["coach-leaders-command", user?.id, isDemoLeader]],
      });
    });
  }, [
    capturePrivateMutationAttempt,
    isDemoLeader,
    isCurrentPrivateMutationAttempt,
    isLoading,
    latestPendingTranscriptReview?.id,
    leaderId,
    markTranscriptReviewOpened,
    reauthorizeCurrentPrivateMutation,
    resolvedData,
    scrollToSection,
    sectionParam,
    user?.id,
  ]);

  useEffect(() => {
    if (isLoading || !resolvedData) return;
    if (!actionParam) return;
    const actionKey = `${leaderId || "no-leader"}:${tabParam || "none"}:${actionParam}`;
    if (handledJourneyActionRef.current === actionKey) return;
    handledJourneyActionRef.current = actionKey;

    const normalized = actionParam.toLowerCase();
    if (
      normalized === "prepare-next"
      || normalized === "prepare-next-behavior"
      || normalized === "override"
      || normalized === "assign"
      || normalized === "graduate"
      || normalized === "extend"
      || normalized === "reorder"
      || normalized === "suggest"
      || normalized === "propose"
    ) {
      scrollToSection("emerging-patterns");
    }
  }, [actionParam, isLoading, leaderId, resolvedData, scrollToSection, tabParam]);

  const continuity = resolvedData?.continuity ?? null;
  const latestCoachGrowthRecord = continuity?.latestCoachGrowthRecord ?? prepRuntime.latestCoachGrowthRecord;
  const behavioralObservation = resolvedData?.behavioralObservation;
  const interventionMemory = continuity?.interventionMemory ?? resolvedData?.interventionMemory;
  const handleOpenTranscriptReviewPage = useCallback(() => {
    const returnTo = leaderId
      ? `/coach/leaders/${encodeURIComponent(leaderId)}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      : null;
    if (isDemoLeader) {
      scrollWithinWorkspace("review", "section-transcript-review");
      return;
    }
    if (latestPendingTranscriptReview?.id) {
      navigate(buildCoachTranscriptReviewRoute(latestPendingTranscriptReview.id, { leaderId, from: "leaders", returnTo }));
      return;
    }
    if (latestCoachGrowthRecord?.transcriptUploadId) {
      navigate(buildCoachTranscriptReviewRoute(latestCoachGrowthRecord.transcriptUploadId, { leaderId, from: "leaders", returnTo }));
      return;
    }
    scrollWithinWorkspace("review", "section-transcript-review");
  }, [
    latestCoachGrowthRecord?.transcriptUploadId,
    isDemoLeader,
    latestPendingTranscriptReview?.id,
    leaderId,
    navigate,
    searchParams,
    scrollWithinWorkspace,
  ]);
  const behavioralObservationName =
    parseCanonicalBehaviorName(behavioralObservation)
    || latestCoachGrowthRecord?.workedOn[0]
    || agreementRecord.namedAgreements[0]
    || null;
  const sessionContinuitySynthesis = useMemo(
    () =>
      continuity
        ? {
            ...continuity,
            latestCoachGrowthRecord,
            interventionMemory,
            reviewLabel: "Open review",
            onOpenReview: latestCoachGrowthRecord?.transcriptUploadId
              ? () => {
                  navigate(buildCoachPostMeetingRoute(latestCoachGrowthRecord.transcriptUploadId!, {
                    leaderId,
                    from: "leaders",
                  }));
                }
              : undefined,
          }
        : null,
    [continuity, interventionMemory, latestCoachGrowthRecord, leaderId, navigate],
  );
  const handleOpenSharedCoachVeeraPanel = useCallback(async (
    invoker: HTMLElement | null,
    source: string,
    suggestedQuestion?: string,
    entryPoint: "prep_brief" | "auto_open" = "prep_brief",
    validateLaunchIntent?: () => boolean,
  ) => {
    const selectedLeaderId = resolvedData?.profile.id ?? leaderId;
    if (!selectedLeaderId) return false;
    const attempt = capturePrivateMutationAttempt();
    if (!await reauthorizeCurrentPrivateMutation(attempt)) return false;
    if (!isCurrentPrivateMutationAttempt(attempt)) return false;
    if (validateLaunchIntent && !validateLaunchIntent()) return false;
    const selectedLeaderName = resolvedData?.profile.full_name?.trim();
    openSharedCoachVeeraPanel({
      pageName: "leaders",
      source,
      presentation: {
        kind: "leader_context",
        leaderName: selectedLeaderName || "this leader",
      },
      data: {
        selectedLeaderIds: [selectedLeaderId],
        ...(selectedLeaderName ? { selectedLeaderName } : {}),
        ...(suggestedQuestion?.trim() ? { suggestedQuestion: suggestedQuestion.trim() } : {}),
      },
    }, invoker);
    if (user?.id) {
      void trackCoachVeeraOpened({
        coachId: user.id,
        leaderId: selectedLeaderId,
        surface: "coach_session_prep",
        entryPoint,
      });
    }
    return true;
  }, [
    capturePrivateMutationAttempt,
    isCurrentPrivateMutationAttempt,
    leaderId,
    openSharedCoachVeeraPanel,
    reauthorizeCurrentPrivateMutation,
    resolvedData?.profile.full_name,
    resolvedData?.profile.id,
    user?.id,
  ]);
  const handleLeaderDetailPrimaryAction = useCallback((action: CoachLeaderDetailPrimaryAction) => {
    if (action.kind === "back_to_leaders") {
      navigate("/coach/leaders");
      return;
    }
    if (action.kind === "retry") {
      if (decisionPresentation.status !== "unavailable") {
        void refetchSessionPrep();
        return;
      }
      const kind = decisionPresentation.recoveryAction.kind;
      if (kind === "retry_access") {
        void relationshipAuthorization.reauthorizeAccess();
      } else if (kind === "retry_billing") {
        void Promise.all([refetchPendingBillingResolution(), refetchLeaderProcessingUsage()]);
      } else if (kind === "retry_upcoming" || kind === "retry_context") {
        void coachLeaderSchedule.refetch();
      } else {
        void refetchSessionPrep();
      }
      return;
    }
    if (action.kind === "resolve_billing_transfer") {
      setShowBillingChoices(true);
      return;
    }
    if (action.kind === "view_plans") {
      navigate("/coach/plans");
      return;
    }
    if (action.kind === "review_evidence" || action.kind === "prepare_upcoming_session" || action.kind === "follow_decision") {
      navigate(action.route);
      return;
    }
    if (action.kind === "add_meeting_or_transcript") {
      navigate(`/coach/meetings?leaderId=${encodeURIComponent(leaderId || "")}&action=schedule`);
    }
  }, [
    coachLeaderSchedule,
    decisionPresentation,
    leaderId,
    navigate,
    refetchLeaderProcessingUsage,
    refetchPendingBillingResolution,
    refetchSessionPrep,
    relationshipAuthorization,
  ]);
  const handleDecisionNoticeAction = useCallback(() => {
    if (decisionPresentation.status !== "ready" || !decisionPresentation.notice?.action) return;
    const kind = decisionPresentation.notice.action.kind;
    if (kind === "reveal_billing_resolution") {
      setShowBillingChoices(true);
    } else if (kind === "retry_source_context") {
      void refetchSessionPrep();
    } else {
      void retryUnavailableAuxiliaryDetails();
    }
  }, [decisionPresentation, refetchSessionPrep, retryUnavailableAuxiliaryDetails]);
  const handleOpenLongitudinalSource = useCallback(async (episode: CoachLeaderLongitudinalEpisode) => {
    if (!user?.id || !leaderId || !relationshipAuthorization.relationshipRevision) return;
    const attempt = capturePrivateMutationAttempt();
    const matches = evidenceItems.filter((item) => (
      item.canonicalIdentity.leaderId === leaderId
      && item.canonicalIdentity.sourceType === episode.source.identity.sourceType
      && item.canonicalIdentity.sourceId === episode.source.identity.sourceId
    ));
    if (matches.length !== 1) {
      toast.error("This source is no longer available.");
      return;
    }
    const match = matches[0];
    const authorization = await authorizeCoachEvidenceReference({
      coachId: user.id,
      leaderId,
      relationshipRevision: relationshipAuthorization.relationshipRevision,
      resolvedItem: match,
      expectedAccessGeneration: accessGeneration,
      getCurrentAccessGeneration: () => currentAccessGenerationRef.current,
      reauthorizeRelationship: relationshipAuthorization.reauthorizeAccess,
    });
    if (authorization.status !== "authorized") {
      toast.error(authorization.status === "retryable_error"
        ? "This source could not be verified. Try again."
        : "This source is no longer available.");
      return;
    }
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    await markEvidenceReferenceViewed({
      reference: { label: episode.title, link: match.link, evidenceId: match.id },
      resolvedSource: toCoachReviewResolvedSource(match),
      openedFrom: "leader-detail-longitudinal",
    });
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    openSelectedEvidence(match);
    captureLongitudinalAnalytics("sourceOpened");
  }, [
    accessGeneration,
    capturePrivateMutationAttempt,
    captureLongitudinalAnalytics,
    evidenceItems,
    isCurrentPrivateMutationAttempt,
    leaderId,
    markEvidenceReferenceViewed,
    openSelectedEvidence,
    relationshipAuthorization,
    user?.id,
  ]);
  const handleOpenRecentContextFact = useCallback(async (fact: CoachLeaderDetailRecentContextFact) => {
    if (!user?.id || !leaderId || !relationshipAuthorization.relationshipRevision) return;
    const attempt = capturePrivateMutationAttempt();
    const matches = evidenceItems.filter((item) => (
      item.id === fact.evidenceReference.evidenceId
      && item.canonicalIdentity.leaderId === leaderId
      && item.canonicalIdentity.sourceId === fact.canonicalSourceId
    ));
    if (matches.length !== 1) {
      toast.error("This source is no longer available.");
      return;
    }
    const match = matches[0];
    const authorization = await authorizeCoachEvidenceReference({
      coachId: user.id,
      leaderId,
      relationshipRevision: relationshipAuthorization.relationshipRevision,
      resolvedItem: match,
      expectedAccessGeneration: accessGeneration,
      getCurrentAccessGeneration: () => currentAccessGenerationRef.current,
      reauthorizeRelationship: relationshipAuthorization.reauthorizeAccess,
    });
    if (authorization.status !== "authorized") {
      toast.error(authorization.status === "retryable_error"
        ? "This source could not be verified. Try again."
        : "This source is no longer available.");
      return;
    }
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    await markEvidenceReferenceViewed({
      reference: {
        label: fact.title,
        link: fact.evidenceReference.link,
        evidenceId: fact.evidenceReference.evidenceId,
      },
      resolvedSource: toCoachReviewResolvedSource(match),
      openedFrom: "leader-detail-recent-context",
    });
    if (!isCurrentPrivateMutationAttempt(attempt)) return;
    openSelectedEvidence(match);
  }, [
    accessGeneration,
    capturePrivateMutationAttempt,
    evidenceItems,
    isCurrentPrivateMutationAttempt,
    leaderId,
    markEvidenceReferenceViewed,
    openSelectedEvidence,
    relationshipAuthorization,
    user?.id,
  ]);

  useEffect(() => {
    if (!user?.id || !leaderId || isDemoLeader || !behavioralObservation) return;

    const telemetryKey = JSON.stringify({
      coachId: user.id,
      leaderId,
      transcriptId: latestCoachGrowthRecord?.transcriptUploadId ?? null,
      moment: behavioralObservation.moment,
      pattern: behavioralObservation.pattern ?? null,
      reflection: behavioralObservation.reflection ?? null,
      behaviorName: behavioralObservationName,
      fallbackUsed: behavioralObservation.isFallback,
    });

    if (observationTelemetryKeyRef.current === telemetryKey) return;
    observationTelemetryKeyRef.current = telemetryKey;

    void trackCoachBehavioralObservationViewed({
      coachId: user.id,
      leaderId,
      surface: "coach_session_prep",
      fallbackUsed: behavioralObservation.isFallback,
      behaviorName: behavioralObservationName,
      hasPatternLine: Boolean(behavioralObservation.pattern),
      hasReflectionLine: Boolean(behavioralObservation.reflection),
      transcriptId: latestCoachGrowthRecord?.transcriptUploadId ?? null,
      meetingId: null,
    });
  }, [
    behavioralObservation,
    isDemoLeader,
    behavioralObservationName,
    latestCoachGrowthRecord?.transcriptUploadId,
    leaderId,
    user?.id,
  ]);

  useEffect(() => {
    if (isLoading || !resolvedData) return;
    if (!sectionParam) {
      coachVeeraCompatibilityAttemptRef.current = null;
      return;
    }
    if (sectionParam === "evidence" || sectionParam === "section-evidence") {
      const match = deepLinkedEvidenceItems[0] || null;
      if (match) openSelectedEvidence(match);
      scrollToSection("section-noticing");
      return;
    }
    if (sectionParam === "veera") {
      const compatibilityAttemptKey = coachVeeraCompatibilityIntentKey;
      if (coachVeeraCompatibilityAttemptRef.current === compatibilityAttemptKey) return;
      coachVeeraCompatibilityAttemptRef.current = compatibilityAttemptKey;
      void (async () => {
        const opened = await handleOpenSharedCoachVeeraPanel(
          null,
          "leader_detail_compatibility_route",
          undefined,
          "auto_open",
          () => latestCoachVeeraCompatibilityIntentRef.current === compatibilityAttemptKey,
        );
        if (!opened) return;
        setSearchParams((current) => {
          if (current.get("section") !== "veera") return current;
          const next = new URLSearchParams(current);
          next.delete("section");
          return next;
        }, { replace: true });
      })();
      return;
    }
    scrollToSection(sectionParam);
  }, [
    accessGeneration,
    coachVeeraCompatibilityIntentKey,
    deepLinkedEvidenceItems,
    leaderId,
    isLoading,
    openSelectedEvidence,
    resolvedData,
    scrollToSection,
    sectionParam,
    handleOpenSharedCoachVeeraPanel,
    setSearchParams,
  ]);
  const selectedEvidenceDetailViewModel = prepRuntime.selectedEvidenceDetail;
  useCoachSessionPrepReceipt({
    enabled: Boolean(!isDemoLeader && !authLoading && relationshipAuthorized
      && !relationshipAuthorization.isChecking && (data || prepError) && !isLoading && (showBriefWorkspace || prepError)),
    assemblyKey: `${user?.id}:${leaderId}:${accessGeneration ?? "no-access-generation"}:${prepAssemblyUpdatedAt}:${prepAssemblyErrorUpdatedAt}`,
    sources: data?.contextSources,
    selection: prepRuntime.contextReceiptSelection,
    queryFailed: Boolean(prepError),
  });
  if (
    !isDemoLeader &&
    (authLoading || relationshipAuthorization.isChecking ||
      (relationshipAuthorized && isLoading))
  ) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Loading session prep...</p>
      </div>
    );
  }

  if (
    !isDemoLeader &&
    relationshipAuthorization.isUnavailable
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-sm space-y-3 text-center">
          <p className="text-sm font-medium">
            We couldn't verify access to this coaching context right now.
          </p>
          <p className="text-sm text-muted-foreground">
            Try again or return to Leaders.
          </p>
          <div className="flex flex-col justify-center gap-2 sm:flex-row">
            <Button onClick={() => void relationshipAuthorization.reauthorizeAccess()}>
              Try again
            </Button>
            <Button variant="outline" onClick={() => navigate("/coach/leaders")}>
              Back to Leaders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isDemoLeader && relationshipAuthorized && prepError && !resolvedData) {
    return (
      <div className="p-8 space-y-4">
        <Button variant="ghost" onClick={() => navigate("/coach/leaders")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leaders
        </Button>
        <Card>
          <CardContent className="p-6 space-y-3">
            <p className="font-medium">We couldn't load this coaching workspace.</p>
            <p className="text-sm text-muted-foreground">
              Your relationship access is still active. Try loading the workspace again.
            </p>
            <Button
              variant="outline"
              disabled={isPrepFetching}
              onClick={() => void refetchSessionPrep()}
            >
              {isPrepFetching ? "Trying again..." : "Try again"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!resolvedData) {
    return (
      <div className="p-8 space-y-4">
        <Button variant="ghost" onClick={() => navigate("/coach/leaders")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leaders
        </Button>
        <Card>
          <CardContent className="p-6 space-y-3">
            <p className="text-sm text-muted-foreground">
              This coaching context is no longer available.
            </p>
            <Button variant="outline" onClick={() => navigate("/coach/leaders")}>
              Back to Leaders
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#fbf8f3] text-foreground dark:bg-[#100c17]">
        <div className="mx-auto max-w-[88rem] space-y-9 px-8 py-10">
          <section className="rounded-[1.65rem] border border-[#d8c7ae]/70 bg-white p-6 shadow-[0_24px_70px_-50px_rgba(76,54,28,0.46)] dark:border-[#2b2435] dark:bg-[#15101d] dark:shadow-[0_24px_90px_rgba(0,0,0,0.18)]">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-3">
                <Button variant="ghost" className="text-[#675869] hover:bg-[#f5efe6] hover:text-[#251837] dark:text-[#d9d0e6] dark:hover:bg-[#211a2b] dark:hover:text-white" onClick={handlePageBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {isFocusedWorkspaceView ? "Back to relationship home" : isWorkspaceDetailView ? `Back to ${resolvedData.profile.full_name}` : "Back to Leaders"}
                </Button>
                <div className="flex items-center gap-4">
                  <Avatar className="h-14 w-14">
                    <AvatarFallback className="bg-primary/20 text-primary text-xl font-medium">
                      {getAvatarInitials(resolvedData.profile.full_name, resolvedData.profile.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h1 className="text-h2 text-[#251837] dark:text-[#f2edf7]">{resolvedData.profile.full_name}</h1>
                    <p className="text-[#675869] dark:text-[#a796b8]">{resolvedData.profile.email}</p>
                  </div>
                </div>
              </div>
              {isFocusedWorkspaceView ? (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                  {workspaceView === "record" && (pendingTranscriptReviewItems.length > 0 || pendingEvidenceReviewCount > 0) ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-[#b99573]/45 bg-[#fbf8f3] text-[#4c382c] hover:bg-[#f5ece1] dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f4dfc7] dark:hover:bg-white/[0.08]"
                      onClick={() => setIsReviewHubOpen(true)}
                    >
                      <FileText className="mr-2 size-4" />
                      Open review content
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#b99573]/45 bg-[#fbf8f3] text-[#4c382c] hover:bg-[#f5ece1] dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f4dfc7] dark:hover:bg-white/[0.08]"
                    onClick={(event) => { void handleOpenSharedCoachVeeraPanel(event.currentTarget, "leader_detail_toolbar"); }}
                  >
                    <Sparkles className="mr-2 size-4" />
                    Ask Coach Veera
                  </Button>
                </div>
              ) : null}
            </div>
          </section>

          <div hidden={isFocusedWorkspaceView} className="space-y-9">
          <CoachLeaderDetailWorkspace
            key={`workspace:${resolvedData.profile.id}:${accessGeneration}`}
            leaderName={resolvedData.profile.full_name || "This leader"}
            presentation={leaderDetailPresentation}
            nextMeeting={nextLeaderMeeting}
            nextMeetingStatus={coachLeaderSchedule.isPending
              ? "loading"
              : coachLeaderSchedule.isError
                ? "retryable_error"
                : "ready"}
            unreadMessageCount={unreadMessageError ? null : unreadMessageCount}
            privateNoteOpen={privateNotesOpen}
            privateNoteStatus={notesError ? "unavailable" : "ready"}
            privateNoteEditor={notesError ? null : (
              <CoachNotesSection
                notesByDate={notesByDate}
                newNoteText={newNoteText}
                onNewNoteTextChange={setNewNoteText}
                onSaveNote={handleSaveNote}
                isAdding={isAdding}
                editingNoteId={editingNoteId}
                editingText={editingText}
                onEditingTextChange={setEditingText}
                onStartEditing={startEditing}
                onCancelEditing={() => setEditingNoteId(null)}
                onSaveEdit={handleSaveEdit}
                onDeleteNote={(id) => void handleDeleteNote(id)}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
                formatTimestamp={formatTimestamp}
              />
            )}
            billingResolutionPanel={showBillingChoices && pendingBillingResolution && !pendingBillingResolutionError ? (
              <div aria-label="Resolve billing ownership" className="space-y-4 rounded-2xl border border-amber-300/40 bg-amber-500/5 p-4">
                <p className="text-sm text-muted-foreground">Choose who should manage billing for this coaching relationship.</p>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <Button variant="outline" onClick={() => void handleResolveBillingOwnership(KEEP_SOLO_BILLING)}>Keep leader-paid billing</Button>
                  <Button onClick={() => void handleResolveBillingOwnership(TRANSFER_TO_COACH)}>Transfer billing to coach</Button>
                  <Button variant="ghost" onClick={() => void handleResolveBillingOwnership(DEFER_BILLING_TRANSFER)}>Defer</Button>
                </div>
              </div>
            ) : null}
            operationalNoticePanel={decisionPresentation.status === "ready" && decisionPresentation.notice ? (
              <div className="space-y-3 rounded-2xl border border-amber-300/40 bg-amber-500/5 p-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{decisionPresentation.notice.title}</p>
                  <p className="text-sm leading-6 text-muted-foreground">{decisionPresentation.notice.description}</p>
                </div>
                {decisionPresentation.notice.action ? (
                  <Button type="button" variant="outline" size="sm" onClick={handleDecisionNoticeAction}>
                    {decisionPresentation.notice.action.label}
                  </Button>
                ) : null}
              </div>
            ) : null}
            unavailableDetail={unavailablePrepSourceCopy}
            onRetryContext={() => { void refetchSessionPrep(); }}
            hasReviewContent={pendingTranscriptReviewItems.length > 0 || pendingEvidenceReviewCount > 0}
            onOpenReviewContent={() => setIsReviewHubOpen(true)}
            onPrimaryAction={handleLeaderDetailPrimaryAction}
            onScheduleMeeting={() => navigate(`/coach/meetings?leaderId=${encodeURIComponent(resolvedData.profile.id)}&action=schedule`)}
            onAskCoachVeera={(invoker) => { void handleOpenSharedCoachVeeraPanel(invoker, "leader_detail_toolbar"); }}
            onMessage={() => navigate(`/coach/messages?leaderId=${encodeURIComponent(resolvedData.profile.id)}`)}
            onAddPrivateNote={() => setPrivateNotesOpen(true)}
            onUploadTranscript={() => navigate(`/coach/reviews?upload=1&leaderId=${encodeURIComponent(resolvedData.profile.id)}&from=leader-detail`)}
            onOpenRecentContext={(fact) => { void handleOpenRecentContextFact(fact); }}
          />

          <CoachLeaderLongitudinalInsight
            key={`longitudinal:${resolvedData.profile.id}:${accessGeneration}`}
            read={longitudinalRead}
            onOpenSource={(episode) => { void handleOpenLongitudinalSource(episode); }}
            onAskCoachVeera={(invoker) => {
              void (async () => {
                const opened = await handleOpenSharedCoachVeeraPanel(
                  invoker,
                  "leader_detail_longitudinal",
                  longitudinalRead.state === "historical_signal"
                    || longitudinalRead.state === "emerging_finding"
                    || longitudinalRead.state === "evidence_backed_finding"
                    ? longitudinalRead.finding.question
                    : undefined,
                );
                if (opened) captureLongitudinalAnalytics("askedVeera");
              })();
            }}
            onAddQuestion={(question) => { void handlePreviewCarryForwardQuestion(question); }}
            onRetry={() => { void refetchSessionPrep(); }}
          />

          <nav aria-label="Explore this relationship" className="overflow-hidden rounded-[1.55rem] border border-[#d8c7ae]/70 bg-[#f7f1e9] shadow-[0_20px_60px_-52px_rgba(45,26,66,0.68)] dark:border-[#d9c8ae]/20 dark:bg-[#181024]">
            <div className="flex items-center justify-between border-b border-[#d8c7ae]/70 px-5 py-3.5 dark:border-[#d9c8ae]/20">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7f5b43] dark:text-[#c9ad87]">Continue the relationship</p>
                <p className="mt-1 text-sm text-muted-foreground">Choose the kind of coaching work you need now.</p>
              </div>
              <span aria-hidden="true" className="hidden font-serif text-2xl text-[#7f5b43]/55 sm:block dark:text-[#c9ad87]/55">01—03</span>
            </div>
            <div className="grid divide-y divide-[#d8c7ae]/70 sm:grid-cols-3 sm:divide-x sm:divide-y-0 dark:divide-[#d9c8ae]/20">
              {LEADER_WORKSPACE_DESTINATIONS.map((destination, index) => {
                const Icon = destination.view === "brief" ? CalendarPlus : destination.view === "throughline" ? Layers3 : FileText;
                return (
                  <button
                    key={destination.view}
                    type="button"
                    aria-label={destination.title}
                    aria-describedby={`workspace-destination-${destination.view}-description`}
                    className="group relative min-h-40 overflow-hidden px-5 py-5 text-left transition-colors hover:bg-white/55 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#a576d8] dark:hover:bg-white/[0.04]"
                    onClick={() => openWorkspace(destination.view)}
                  >
                    <span aria-hidden="true" className="absolute inset-x-5 top-0 h-px origin-left scale-x-0 bg-[#9d73cf] transition-transform duration-300 group-hover:scale-x-100 group-focus-visible:scale-x-100" />
                    <span className="flex items-start justify-between gap-4">
                      <span className="flex size-9 items-center justify-center rounded-full border border-[#b99573]/55 bg-white/70 text-[#6d4c37] shadow-sm dark:border-[#d9c8ae]/20 dark:bg-white/[0.05] dark:text-[#e6ceaf]">
                        <Icon aria-hidden="true" className="size-4" />
                      </span>
                      <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.14em] text-[#8a6c55] dark:text-[#c9ad87]">
                        0{index + 1}
                        <ArrowUpRight aria-hidden="true" className="size-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      </span>
                    </span>
                    <span className="mt-7 block max-w-[15rem] font-serif text-xl font-semibold leading-6 tracking-tight text-[#2f1b42] dark:text-[#f7f0e8]">{destination.title}</span>
                    <span id={`workspace-destination-${destination.view}-description`} className="mt-2 block max-w-[16rem] text-sm leading-5 text-[#6d5b6c] dark:text-[#c7b8cc]">{destination.description}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-[#d8c7ae]/70 px-5 py-3.5 dark:border-[#d9c8ae]/20" role="group" aria-label="Relationship record actions">
              <span className="mr-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#7f5b43] dark:text-[#c9ad87]">Evidence &amp; record</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-10 rounded-full border-[#b99573]/45 bg-white/60 px-3 text-[#4c382c] hover:bg-white dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f3e6d5] dark:hover:bg-white/[0.08]"
                disabled={!agreementsAvailable}
                onClick={() => {
                  setAgreementsOpen(true);
                  scrollWithinWorkspace("record", "section-agreements");
                }}
              >
                <FileText className="mr-2 size-3.5" />
                {agreementsAvailable ? "Agreements and follow-ups" : "Agreements unavailable"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="min-h-10 rounded-full border-[#b99573]/45 bg-white/60 px-3 text-[#4c382c] hover:bg-white dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f3e6d5] dark:hover:bg-white/[0.08]"
                onClick={() => navigate(`/coach/leaders/${encodeURIComponent(resolvedData.profile.id)}/record?from=leader-detail`)}
              >
                <Layers3 className="mr-2 size-3.5" />
                Coaching history
              </Button>
            </div>
          </nav>
          </div>

          <Dialog open={carryForwardQuestionPreview !== null} onOpenChange={(open) => {
            if (!open) setCarryForwardQuestionPreview(null);
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add question to the next session</DialogTitle>
                <DialogDescription>Review the merge before saving. This changes only your private carry-forward note.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm leading-6 text-muted-foreground">
                {carryForwardNoteText.trim()
                  ? <p><span className="font-medium text-foreground">Existing note:</span> {carryForwardNoteText}</p>
                  : <p>No existing private carry-forward note.</p>}
                <p><span className="font-medium text-foreground">Question to add:</span> {carryForwardQuestionPreview}</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCarryForwardQuestionPreview(null)}>Cancel</Button>
                <Button type="button" onClick={() => { void handleSaveCarryForwardQuestion(); }} disabled={isSavingCarryForwardState}>Save to private note</Button>
              </div>
            </DialogContent>
          </Dialog>


          {selectedWorkspaceCopy ? (
            <section aria-label={`${selectedWorkspaceCopy.title} workspace`} className="overflow-hidden rounded-[1.65rem] border border-[#d8c7ae]/70 bg-white shadow-[0_24px_70px_-50px_rgba(76,54,28,0.46)] dark:border-[#2b2435] dark:bg-[#15101d] dark:shadow-[0_24px_90px_rgba(0,0,0,0.18)]">
              <div className="border-b border-[#d8c7ae]/70 bg-[#f7f1e9] px-6 py-5 dark:border-[#2b2435] dark:bg-[#1a1225]">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#7f5b43] dark:text-[#c9ad87]">Relationship workspace</p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#7f5b43] dark:text-[#9b8aad]">{selectedWorkspaceCopy.eyebrow}</p>
                    <h2 className="font-serif text-3xl font-semibold tracking-tight text-[#251837] dark:text-[#f2edf7]">{selectedWorkspaceCopy.title}</h2>
                  </div>
                  <p className="max-w-xl text-sm leading-6 text-[#675869] dark:text-[#a796b8]">{selectedWorkspaceCopy.description}</p>
                </div>
              </div>
            </section>
          ) : null}

          {showReviewWorkspace && practiceFocusReviewItems.length > 0 ? (
            <section id="section-practice-focus-review">
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Practice Focus</p>
                      <CardTitle className="text-h3">Approve what the leader should practice next</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Review inferred focuses before they become visible in the leader app.
                      </p>
                    </div>
                    <Badge variant="outline" className="w-fit border-primary/30 bg-background/80 text-primary">
                      {practiceFocusReviewItems.length} pending
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {practiceFocusReviewItems.map((item) => {
                    const leaderSafeEvidenceCount = item.evidence.filter((evidence) => evidence.leaderSafeSummary).length;
                    return (
                      <div
                        key={item.id}
                        className="rounded-lg border border-border/60 bg-background p-4"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                              {item.confidenceLevel ? (
                                <Badge variant="secondary">{item.confidenceLevel} confidence</Badge>
                              ) : null}
                            </div>
                            <p className="text-sm text-foreground">{item.practicePrompt}</p>
                            {item.rationaleSummary ? (
                              <p className="text-sm text-muted-foreground">{item.rationaleSummary}</p>
                            ) : null}
                            <div className="space-y-1">
                              {item.evidence.length > 0 ? item.evidence.slice(0, 2).map((evidence) => (
                                <p key={evidence.id} className="text-xs text-muted-foreground">
                                  {evidence.leaderSafeSummary || "Needs a leader-safe summary before sharing."}
                                </p>
                              )) : (
                                <p className="text-xs text-muted-foreground">No leader-safe evidence summary is ready yet.</p>
                              )}
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
                            <Button
                              type="button"
                              onClick={() => { void handleApprovePracticeFocus(item); }}
                              disabled={leaderSafeEvidenceCount === 0 || approvePracticeFocusSuggestion.isPending}
                            >
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              Approve for leader
                            </Button>
                            <Badge variant="outline" className="justify-center">
                              {leaderSafeEvidenceCount} ready evidence item{leaderSafeEvidenceCount === 1 ? "" : "s"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </section>
          ) : null}

          <div hidden={showWorkspaceMap} className="space-y-6">
          {showReviewWorkspace ? (
          <section
            aria-labelledby="leader-review-work-heading"
            className="rounded-lg border border-violet-300/25 bg-violet-500/5 p-4 shadow-sm sm:p-5"
          >
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Leader workspace</p>
                <h2 id="leader-review-work-heading" className="text-xl font-semibold text-foreground">
                  Review Work
                </h2>
              </div>
              <Badge variant="outline" className="w-fit">
                {pendingTranscriptReviewItems.length + pendingEvidenceReviewCount > 0
                  ? `${pendingTranscriptReviewItems.length + pendingEvidenceReviewCount} items open`
                  : "All review work completed"}
              </Badge>
            </div>
            <div className="grid gap-4">
              <section id="section-transcript-review">
                <Card className="border-border/60 bg-background/95">
              <CardHeader className="pb-2">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-h3">Leader Transcript Review</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Review leader-specific transcripts from this profile so the evidence, agreements, and next coaching moves stay in one place.
                    </p>
                  </div>
                  {pendingTranscriptReviewItems.length > 0 ? (
                    <Badge className="w-fit border-amber-300/40 bg-amber-500/10 text-amber-700 dark:text-amber-200">
                      {pendingTranscriptReviewItems.length} awaiting review
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="w-fit">
                      No pending transcript review
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-4">
                {latestPendingTranscriptReview ? (
                  <>
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2">
                      <p className="font-medium text-foreground">{latestPendingTranscriptReview.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatTimestamp(latestPendingTranscriptReview.created_at)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {pendingTranscriptReviewItems.length === 1
                          ? "This transcript is ready for coach review from the leader context."
                          : "Open the latest transcript review here, then use the evidence sections below to compare related meeting context."}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button onClick={handleOpenTranscriptReviewPage}>
                        Open transcript page
                      </Button>
                      <Button variant="outline" onClick={handleBrowseEvidence}>
                        Browse evidence
                      </Button>
                    </div>
                  </>
                ) : latestCoachGrowthRecord?.transcriptUploadId ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      No transcript is currently awaiting review. The most recent saved review still lives with this leader's coaching context.
                    </p>
                    <div>
                      <Button variant="outline" onClick={handleOpenTranscriptReviewPage}>
                        Open latest transcript page
                      </Button>
                    </div>
                  </>
                ) : latestReviewedTranscript ? (
                  <p className="text-sm text-muted-foreground">
                    No transcript is currently awaiting review. Recent transcript evidence is already reflected below in this leader's prep context.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No transcript evidence is attached to this leader yet. New transcript reviews will appear here once a session is uploaded and analyzed.
                  </p>
                )}
              </CardContent>
                </Card>
              </section>

              <section id="section-whats-new">
                <Card className="border-border/60 bg-background/95">
              <CardHeader className="pb-2">
                <CardTitle className="text-h3">What&apos;s New Since Last Visit</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {sectionNewCounts.total === 0 ? (
                  <p className="text-sm text-muted-foreground">No new evidence has been added since your last acknowledgement.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-border/60 p-3">
                      <p className="text-xs text-muted-foreground">Transcripts</p>
                      <p className="text-2xl font-semibold">{sectionNewCounts.transcripts}</p>
                    </div>
                    <div className="rounded-lg border border-border/60 p-3">
                      <p className="text-xs text-muted-foreground">Reflections</p>
                      <p className="text-2xl font-semibold">{sectionNewCounts.reflections}</p>
                    </div>
                    <div className="rounded-lg border border-border/60 p-3">
                      <p className="text-xs text-muted-foreground">Artifacts</p>
                      <p className="text-2xl font-semibold">{sectionNewCounts.artifacts}</p>
                    </div>
                  </div>
                )}
              </CardContent>
                </Card>
              </section>
            </div>
          </section>
          ) : null}

          {showThroughlineWorkspace || showPatternsWorkspace ? (
          <section
            aria-labelledby="leader-intelligence-heading"
            className="rounded-lg border border-teal-300/25 bg-teal-500/5 p-4 shadow-sm sm:p-5"
          >
            <div className="mb-4 space-y-1">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Longitudinal context</p>
                <h2 id="leader-intelligence-heading" className="text-xl font-semibold text-foreground">
                  Coaching Intelligence
                </h2>
              </div>
            </div>
            <div className="grid gap-4">
              {showThroughlineWorkspace ? (
              <section id="section-throughline">
                <CoachThroughlineSection
                  points={throughlineArcPoints}
                  chartLabelPoints={chartLabelPoints}
                  selectedDayKey={selectedThroughlineDay}
                  selectedDayViewModel={selectedThroughlineDayViewModel}
                  windowDays={throughlineWindowDays}
                  stateCounts={throughlineStateCounts}
                  gapAlert={throughlineGapAlert}
                  sparseWindowAutoExpanded={sparseWindowAutoExpanded}
                  onSelectDay={handleThroughlineNodeSelect}
                  onSelectWindowDays={setThroughlineWindowDays}
                  onOpenAllSources={handleThroughlineOpenAllSources}
                  onAskVeeraAboutDay={() => void handleAskVeeraAboutSelectedDay()}
                />
              </section>
              ) : null}

              {showThroughlineWorkspace ? (
              <section id="section-snapshot">
                <EvidenceSnapshotSection
                  snapshot={snapshot}
                  onReviewLatestEvidence={handleReviewLatestEvidence}
                  onOpenTranscripts={handleOpenTranscriptReviewPage}
                  onOpenEvidenceRecord={() => openWorkspace("record")}
                  onGenerateCoachVeera={() => {
                    void handleOpenSharedCoachVeeraPanel(
                      null,
                      "leader_detail_evidence_snapshot",
                      DEFAULT_COACH_VEERA_QUESTION,
                    );
                  }}
                  isGeneratingSenseMaking={false}
                />
              </section>
              ) : null}

              {showPatternsWorkspace ? (
              <section id="section-emerging-patterns">
                {!isDemoLeader && leaderId && relationshipAuthorized ? <BiThemeReviewPanel leaderId={leaderId} accessGeneration={accessGeneration} /> : null}
                <EmergingPatternsSection
                  section={emergingPatternsSection}
                  leaderId={leaderId || undefined}
                  coachVeeraSlot={portfolioIntelligence.themes.length > 0 ? (
                    <CoachPortfolioIntelligenceCard
                      intelligence={portfolioIntelligence}
                      compact
                      onOpenLeaderTieBack={(reference) => navigate(reference.route)}
                    />
                  ) : null}
                  onOpenEvidenceReference={handleOpenEvidenceReference}
                />
              </section>
              ) : null}
            </div>
          </section>
          ) : null}

          {showBriefWorkspace || showArcWorkspace ? (
          <section
            id="section-prepare"
            aria-labelledby="leader-prepare-heading"
            className="rounded-lg border border-amber-300/25 bg-amber-500/5 p-4 shadow-sm sm:p-5"
          >
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Next conversation</p>
                <h2 id="leader-prepare-heading" className="text-xl font-semibold text-foreground">
                  Prepare the Session
                </h2>
              </div>
              <Badge variant="outline" className="w-fit border-amber-300/30 bg-amber-500/10 text-amber-700 dark:text-amber-200">
                Intervention Options
              </Badge>
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.82fr)]">
              {showBriefWorkspace ? (
              <CoachVeeraPrepBriefCard
                brief={prepRuntime.veeraInputs.fallbackBrief}
                savedCarryForwardNote={carryForwardState?.note ?? null}
                carryForwardLoadError={carryForwardLoadError}
                isLoadingCarryForward={isLoadingCarryForward}
                onRetryCarryForward={() => { void refreshCarryForwardState(); }}
                reviewedCarryForward={(reviewedCarryForward ?? []).map((review) => ({
                  id: review.id,
                  title: review.reframedTitle ?? review.originalTitle,
                }))}
                carryForwardUpdatedAt={carryForwardState?.updated_at ?? null}
                carryForwardNote={relationshipReadEnabled ? carryForwardNoteText : ""}
                onCarryForwardNoteChange={setCarryForwardNoteText}
                onSaveCarryForwardNote={() => { void handleSaveCarryForwardNote(); }}
                isSavingCarryForwardNote={isSavingCarryForwardState}
                visibilityState={carryForwardVisibilityState}
                onVisibilityStateChange={setCarryForwardVisibilityState}
                onOpenEvidenceReference={(reference) =>
                  handleOpenEvidenceReference({
                    label: reference.label,
                    link: reference.link,
                    evidenceId: reference.evidenceId ?? null,
                    sourceRevision: reference.sourceRevision,
                    source: "coach_veera_brief",
                  })}
              />
              ) : null}
              {showArcWorkspace ? (
              <div className="grid gap-4">
                <section id="section-leader-arc" className="grid gap-4">
                  <LeaderArcSection
                    resource={prepRuntime.veeraInputs.leaderArc}
                    onOpenEvidenceReference={(reference) =>
                      handleOpenEvidenceReference({
                        label: reference.label,
                        link: reference.link,
                        evidenceId: reference.evidenceId ?? null,
                        sourceRevision: reference.sourceRevision,
                        source: "coach_veera_brief",
                      })}
                  />
                  <CoachSessionPrepInterventionOptionsCard
                    options={prepRuntime.veeraInputs.interventionOptions}
                    onOpenEvidenceReference={(reference) =>
                      handleOpenEvidenceReference({
                        label: reference.label,
                        link: reference.link,
                        evidenceId: reference.evidenceId ?? null,
                        sourceRevision: reference.sourceRevision,
                        source: "coach_veera_brief",
                      })}
                  />
                </section>
              </div>
              ) : null}
            </div>
          </section>
          ) : null}

          <section id="section-evidence" aria-hidden className="sr-only" />

          {showRecordWorkspace ? (
          <section
            aria-labelledby="leader-record-heading"
            className="rounded-lg border border-rose-300/20 bg-rose-500/5 p-4 shadow-sm sm:p-5"
          >
            <div className="mb-4 space-y-1">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Longitudinal record</p>
              <h2 id="leader-record-heading" className="text-xl font-semibold text-foreground">
                Continuity and Agreements
              </h2>
            </div>
            <div className="grid gap-4">
              <section id="section-leadership-record">
                <LeadershipRecordTimeline items={prepRuntime.leadershipRecordItems} />
              </section>

              <section id="section-coach-growth">
                <ContinuityPanel
                  synthesis={sessionContinuitySynthesis}
                  recordSummary={coachingRecordSummary}
                  runtime={!sessionContinuitySynthesis ? buildCoachSessionContinuityRuntime({
                    namedAgreements: agreementRecord.namedAgreements,
                    latestCoachGrowthRecord,
                    leaderName: resolvedData.profile.full_name,
                    recordSummary: coachingRecordSummary,
                  }) : undefined}
                  className="border-border/60"
                />
              </section>

              <section id="section-agreements">
                {leaderId && user?.id ? <div className="mb-5"><CoachDevelopmentCard leaderId={leaderId} requestedPublicationId={searchParams.get("development") || undefined}/></div> : null}
                {sharedSessionParam && leaderId && user?.id ? <div id="selected-shared-session" className="mb-5"><CoachSharedSessionView key={`${user.id}:${leaderId}:${sharedSessionParam}`} recordId={sharedSessionParam} leaderId={leaderId} coachId={user.id} publicationId={searchParams.get("learning") || undefined} focusStepId={searchParams.get("step") || undefined} focusItemId={searchParams.get("response") || undefined}/></div> : null}
                <Card className="border-border/60">
              <Collapsible open={agreementsOpen} onOpenChange={setAgreementsOpen}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="pb-2 cursor-pointer hover:bg-muted/30 transition-colors rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-h3">Save This Conversation</CardTitle>
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-180" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="p-6 pt-0 space-y-4">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Named agreements</p>
                      {agreementRecord.namedAgreements.length > 0 ? (
                        agreementRecord.namedAgreements.map((line, idx) => (
                          <div key={`${line}-${idx}`}>
                            <p className="text-sm text-foreground">- {line}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No explicit agreements captured yet.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Recent conversation records</p>
                      {agreementRecord.conversationRecords.length > 0 ? (
                        agreementRecord.conversationRecords.map((record) => (
                          <div key={record.id} id={record.transcriptUploadId ? `agreement-transcript-${record.transcriptUploadId}` : undefined} className="rounded-lg border border-border/60 p-3 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs text-muted-foreground">{record.source}</p>
                              <p className="text-xs text-muted-foreground">{formatTimestamp(record.created_at)}</p>
                            </div>
                            <p className="text-sm text-foreground">{record.summary}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No coach-leader conversation records yet.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
                </Card>
              </section>

            </div>
          </section>
          ) : null}

          {showRecordWorkspace ? (
          <section
            id="section-notes"
            aria-labelledby="leader-notes-heading"
            className="rounded-lg border border-border/60 bg-muted/10 p-4 shadow-sm sm:p-5"
          >
            <div className="mb-4 space-y-1">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Private context</p>
              <h2 id="leader-notes-heading" className="text-xl font-semibold text-foreground">
                Coach Notes
              </h2>
            </div>
            <CoachNotesSection
              notesByDate={notesByDate}
              newNoteText={newNoteText}
              onNewNoteTextChange={setNewNoteText}
              onSaveNote={handleSaveNote}
              isAdding={isAdding}
              editingNoteId={editingNoteId}
              editingText={editingText}
              onEditingTextChange={setEditingText}
              onStartEditing={startEditing}
              onCancelEditing={() => setEditingNoteId(null)}
              onSaveEdit={handleSaveEdit}
              onDeleteNote={(id) => void handleDeleteNote(id)}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              formatTimestamp={formatTimestamp}
            />
          </section>
          ) : null}
          </div>
        </div>
      </div>
      <Dialog open={isReviewHubOpen} onOpenChange={setIsReviewHubOpen}>
        <DialogContent className="max-h-[90vh] w-[min(96vw,72rem)] max-w-none overflow-hidden border-border/70 bg-background p-0 shadow-[0_30px_120px_rgba(0,0,0,0.32)] dark:bg-[#171321] dark:shadow-[0_30px_120px_rgba(0,0,0,0.55)]">
          <div className="max-h-[90vh] overflow-y-auto bg-[radial-gradient(circle_at_top_right,rgba(169,128,255,0.10),transparent_38%),radial-gradient(circle_at_top_left,rgba(245,182,76,0.08),transparent_32%)] dark:bg-[radial-gradient(circle_at_top_right,rgba(169,128,255,0.16),transparent_38%),radial-gradient(circle_at_top_left,rgba(245,182,76,0.12),transparent_32%)]">
            <DialogHeader className="border-b border-border/60 px-6 py-5 text-left">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <Layers3 className="h-3.5 w-3.5" />
                    Review hub
                  </div>
                  <DialogTitle className="text-2xl">What needs attention right now</DialogTitle>
                  <DialogDescription className="max-w-2xl text-sm leading-6">
                    Pick the area you want to review next. You stay in this leader context until you deliberately open the transcript page.
                  </DialogDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:max-w-[14rem] sm:justify-end sm:text-right">
                  <Badge variant="outline" className="border-primary/20 bg-primary/5 text-primary">
                    {pendingTranscriptReviewItems.length > 0 ? `${pendingTranscriptReviewItems.length} transcripts` : "No transcripts"}
                  </Badge>
                  <Badge variant="outline" className="border-amber-300/30 bg-amber-500/10 text-amber-700 dark:text-amber-200">
                    {pendingEvidenceReviewCount > 0 ? `${pendingEvidenceReviewCount} evidence items` : "No evidence items"}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => { void handleMarkSeen(); }}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark all reviewed
                  </Button>
                  <DialogClose asChild>
                    <Button variant="ghost" size="sm" aria-label="Close review hub">
                      <X className="h-4 w-4 mr-2" />
                      Close review hub
                    </Button>
                  </DialogClose>
                </div>
              </div>
            </DialogHeader>
            <div className="grid gap-3 px-6 py-6 md:grid-cols-3">
            <button
              type="button"
              className="group relative overflow-hidden rounded-[1.4rem] border border-primary/30 bg-[linear-gradient(180deg,rgba(169,128,255,0.16),rgba(255,255,255,0.02))] p-4 text-left shadow-[0_18px_50px_rgba(0,0,0,0.22)] transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_22px_60px_rgba(169,128,255,0.16)]"
              onClick={() => {
                setIsReviewHubOpen(false);
                handleOpenTranscriptReviewPage();
              }}
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent" />
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-full border border-primary/25 bg-primary/12 p-2.5 text-primary shadow-inner">
                  <FileText className="h-4.5 w-4.5" />
                </div>
                <div className="rounded-full border border-primary/20 bg-background/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                  Primary
                </div>
              </div>
              <p className="mt-4 text-sm font-semibold text-foreground">Transcript review</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground/90">
                {latestPendingTranscriptReview
                  ? latestPendingTranscriptReview.title
                  : "Jump back to the transcript review section for the latest completed review."}
              </p>
              <p className="mt-4 text-xs font-medium text-primary">
                {pendingTranscriptReviewItems.length > 0
                  ? `${pendingTranscriptReviewItems.length} transcript${pendingTranscriptReviewItems.length === 1 ? "" : "s"} waiting`
                  : "Open the transcript section"}
              </p>
            </button>
            <button
              type="button"
              className="group rounded-[1.4rem] border border-amber-300/30 bg-[linear-gradient(180deg,rgba(245,182,76,0.10),rgba(255,255,255,0.02))] p-4 text-left transition-all hover:-translate-y-1 hover:border-amber-300/45 hover:shadow-[0_16px_44px_rgba(245,182,76,0.10)]"
              onClick={() => {
                setIsReviewHubOpen(false);
                setIsEvidenceReviewDialogOpen(true);
                handleBrowseEvidence();
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-full border border-amber-300/20 bg-amber-500/12 p-2.5 text-amber-600">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
              <p className="mt-4 text-sm font-semibold text-foreground">Evidence review</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground/90">
                {pendingEvidenceReviewCount > 0
                  ? `${pendingEvidenceReviewCount} evidence item${pendingEvidenceReviewCount === 1 ? "" : "s"} waiting`
                  : "Open the evidence panel for related context."}
              </p>
              <p className="mt-4 text-xs font-medium text-primary">
                {pendingEvidenceReviewCount > 0 ? "Open evidence" : "Browse evidence"}
              </p>
            </button>
            <button
              type="button"
              className="group rounded-[1.4rem] border border-border/60 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] p-4 text-left transition-all hover:-translate-y-1 hover:border-primary/25 hover:bg-muted/30"
              onClick={() => {
                setIsReviewHubOpen(false);
                setAgreementsOpen(true);
                scrollWithinWorkspace("record", "section-agreements");
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="rounded-full border border-border/60 bg-background/80 p-2.5 text-muted-foreground">
                  <Layers3 className="h-4 w-4" />
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
              <p className="mt-4 text-sm font-semibold text-foreground">Agreement record</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground/90">
                Review saved coach-leader agreement notes and conversation records.
              </p>
              <p className="mt-4 text-xs font-medium text-primary">Open agreements</p>
            </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isEvidenceReviewDialogOpen} onOpenChange={setIsEvidenceReviewDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Evidence to review</DialogTitle>
            <DialogDescription>
              Review the remaining evidence items for this leader. Opening one marks that item reviewed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {unreviewedEvidenceQueue.length === 0 ? (
              <div className="rounded-lg border border-border/60 p-4">
                <p className="text-sm font-medium text-foreground">Everything here is already reviewed.</p>
                <p className="text-sm text-muted-foreground">New evidence will appear in this box only when it still needs coach review.</p>
              </div>
            ) : (
              unreviewedEvidenceQueue.map((item) => (
                <div key={item.artifactKey} className="rounded-lg border border-border/60 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.label}</p>
                      {item.matchedEvidence ? (
                        <p className="text-xs text-muted-foreground">
                          {item.matchedEvidence.source} • {formatTimestamp(item.matchedEvidence.created_at)}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Source detail will open in the evidence panel.</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        void handleOpenEvidenceReference({
                          label: item.label,
                          link: item.link,
                          evidenceId: item.evidenceId,
                          returnToQueue: true,
                        });
                        setIsEvidenceReviewDialogOpen(false);
                      }}
                    >
                      Open evidence
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      <EvidenceDetailDialog
        evidence={selectedEvidence}
        onClose={closeSelectedEvidence}
        showDoneAction={selectedEvidenceSession?.returnToQueue === true && selectedEvidence?.source !== "Transcript"}
        onDone={closeSelectedEvidence}
        formatTimestamp={formatTimestamp}
        onNavigate={navigate}
        linkLabel={selectedEvidenceDetailViewModel.linkLabel}
        contextLine={selectedEvidenceDetailViewModel.contextLine}
      />
    </>
  );
}
